import ReconciliationWorkspace from './reconciliation/ReconciliationWorkspace';
import useBankSuggestionRefresh from '../../../hooks/useBankSuggestionRefresh';
import { bankTransactionLabel, bankTransactionDate, bankPaymentMessage, bankSecondaryFacts, bankTextSourceLabel } from '../../../utils/bankTransactionText';
import BankTransactionFacts from './BankTransactionFacts';
import KonteringSlip from './KonteringSlip';
import { formatCurrency, formatDate } from './bankFormat';
import HistoricalBookingEvidence from './HistoricalBookingEvidence';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Dropdown, Form, Modal, OverlayTrigger, Popover, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'sonner';
import moment from 'moment';

import { brandSelectTheme } from '../Common/reactSelectTheme';
import { resolveRoleAccounts } from '../../../services/api/chartRolesApi';

import {
    Landmark,
    RefreshCw,
    Users,
    CheckCircle,
    CheckCircle2,
    ChevronDown,
    ChevronLeft,
    ChevronUp,
    AlertCircle,
    AlertTriangle,
    Info,
    MinusCircle,
    Sparkles,
    TrendingUp,
    TrendingDown,
    FilePlus,
    FileText,
    Lightbulb,
    MoreVertical,
    SlidersHorizontal,
    Undo2,
    Upload,
    X as XIcon,
} from 'lucide-react';

import PageHeader from '../../layouts/PageHeader';
import { ListToolbar, FilterChips } from '../Common/ListToolbar';
import { EmptyState } from '../Common/EmptyState';
import SideSheet from '../Common/SideSheet';
import { BankLogo } from '../Settings/BankConnectModal';
import { fetchBanks } from '../../../services/BankConnectionService';
import { useListControls } from '../../../hooks/useListControls';
import BankImportModal from './BankImportModal';
import BookingEditor from './booking/BookingEditor';
import useBookingDraft from '../../../hooks/useBookingDraft';
import usePurchaseAction from './reconciliation/usePurchaseAction';
import VerificationDetailsModal from '../Accounting/Verification/VerificationDetailsModal';
import {
    listBankStatements,
    getBankStatement,
    bulkMarkReconciled,
    extractListTotal,
    getUnifiedSuggestions,
    getPaymentKontering,
    confirmSuggestion,
    resetStatement,
    submitMatchingFeedback,
    getStatementVerification,
    getBankAccountMappings,
    getVerificationById,
    getLlmSuggestion,
    getAttentionCounts,
    linkSafeMatches,
    bulkUnmatchStatements,
    bookSimilar,
    getSimilarCount,
    restoreRejectedSuggestions,
    buildConfirmTargets,
    buildHistoryPrefillRows,
    hasStrongSuggestion,
    isTransferSuggestion,
    llmDenialKind,
    normalizeSuggestionRows,
    strategyLabel,
    confidenceTier,
    confidenceLabel,
    priorsEvidenceText,
    deriveDisplayState,
    sortRowsByAccount,
    ATTENTION_STATES,
    DISPLAY_BOOKED,
    DISPLAY_TO_CONFIRM,
    DISPLAY_SUGGESTED,
    DISPLAY_NEEDS_YOU,
    DISPLAY_CLEARED,
    DISPLAY_SKIPPED,
    TIER_MATCH,
    TIER_REVIEW,
    TIER_DONE,
} from '../../../services/MatchingService';
import {
    getRuleProposals,
    dismissRuleProposal,
    findMatchingProposal,
    proposalToFormRule,
} from '../../../services/InsightsService';
import { useFeatures } from '../../../context/FeatureContext';
import {
    bankTransactionDescriptor,
    reconciliationQueueItemDescriptor,
    handOffState,
    useSurfaceTaskDescriptor,
} from '../../../context/TaskContextDescriptor';

// The server caps unpaginated list calls (default 50) — fetch a big page
// explicitly so the queue sees the whole backlog. Real paging is a later
// iteration; until then we tell the user when the backlog is even bigger
// than this window.
const QUEUE_FETCH_LIMIT = 500;

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Bankhändelser — THE bank-transaction home (UX redesign Phase C,
 * docs/TRANSACTION_MATCHING_UX_REDESIGN.md §4.3). One page merges the old
 * Matchning queue and the Banktransaktioner ledger:
 *
 *   - Default filter "Att hantera": the exception inbox — rows that need
 *     the user (att bekräfta / förslag finns / behöver dig).
 *   - "Alla": the full ledger view with server-side paging ("Ladda fler").
 *   - "Klara": the done pile (bokförd / avstämd / överhoppad).
 *
 * Every row carries a categorical display_state badge (§4.2, derived
 * server-side from status + the persisted suggestion snapshot — never a
 * raw percentage). Import is the ONLY manual step: the backend auto-runs
 * the matching engine on import (B3), and this page silently runs it once
 * when it finds rows the engine never saw. Undo lives on the row.
 */

// Date-window presets (owner 2026-07-27): coarse noise filter over the
// whole page — pills, counts and batch actions all follow the window.
const DATE_RANGE_OPTIONS = [
    { value: 'all', label: 'Alla datum' },
    { value: 'm1', label: 'Senaste månaden' },
    { value: 'm3', label: 'Senaste 3 mån' },
    { value: 'year', label: 'I år' },
    { value: 'custom', label: 'Anpassat…' },
];

const FILTER_ATTENTION = 'attention';
const FILTER_MATCHED = 'matched';
const FILTER_ALL = 'all';
const FILTER_DONE = 'done';
const FILTERS = [
    { key: FILTER_ATTENTION, label: 'Att hantera' },
    { key: FILTER_MATCHED, label: 'Att koppla' },
    { key: FILTER_ALL, label: 'Alla' },
    { key: FILTER_DONE, label: 'Klara' },
];

// Optimistic overlay over the server-side rejection suppression (Phase B):
// the server filters rejected suggestions on every fetch; this set hides
// one the instant the user rejects it, before the refetch lands.
const rejectionKey = (lineId, s) =>
    `${lineId}::${s?.candidateId || s?.matchStrategy || s?.strategy || 'x'}`;

// The five categorical user-facing states (§4.2) — colors get tokenized in
// the Phase E visual pass; labels are the contract.
const DISPLAY_CONFIG = {
    [DISPLAY_BOOKED]: { label: 'Bokförd', color: '#10b981', icon: CheckCircle },
    [DISPLAY_TO_CONFIRM]: { label: 'Att bekräfta', color: '#b45309', icon: CheckCircle },
    [DISPLAY_SUGGESTED]: { label: 'Förslag', color: 'var(--primary)', icon: Lightbulb },
    [DISPLAY_NEEDS_YOU]: { label: 'Behöver dig', color: '#dc2626', icon: AlertCircle },
    [DISPLAY_CLEARED]: { label: 'Avstämd', color: '#6b7280', icon: CheckCircle2 },
    [DISPLAY_SKIPPED]: { label: 'Överhoppad', color: '#9ca3af', icon: MinusCircle },
};

// Statuses the batch action confirms, and statuses "Ångra" can reset.
const BATCH_CONFIRM_STATUSES = ['auto_matched', 'manual_matched'];
const RESETTABLE_STATUSES = ['auto_matched', 'manual_matched', 'reconciled_external', 'skipped'];

// The batch target set ("Granska och godkänn"): rows the system is sure
// about. Three families, one review surface — the row's own label says
// what happens to it (Kopplad/Koppla/Förslag vocabulary).
const isBatchConfirmable = (l) => BATCH_CONFIRM_STATUSES.includes(l.reconciliation_status);
// Att koppla holds rows whose booking already exists. An invoice match is
// not one: confirming it registers a payment, one row at a time, so it
// stays under Att hantera with the supplier invoices.
const isLinkSafe = (l) => l.reconciliation_status === 'unmatched'
    && l.suggestion_snapshot?.strategy === 'verification'
    && l.suggestion_snapshot?.tier === 'high';

// List ordering (Inkorg parity). 'Behöver dig först' surfaces the rows the
// system is explicitly asking about, then engine matches to confirm, then
// suggestions.
const SORT_OPTIONS = [
    { value: 'date_desc', label: 'Senaste först' },
    { value: 'date_asc', label: 'Äldst först' },
    { value: 'amount_desc', label: 'Största belopp' },
    { value: 'amount_asc', label: 'Minsta belopp' },
    { value: 'state', label: 'Behöver dig först' },
];
const STATE_ORDER = {
    [DISPLAY_NEEDS_YOU]: 0,
    [DISPLAY_TO_CONFIRM]: 1,
    [DISPLAY_SUGGESTED]: 2,
    [DISPLAY_BOOKED]: 3,
    [DISPLAY_CLEARED]: 4,
    [DISPLAY_SKIPPED]: 5,
};

// Detail-pane mode for a display state: to_confirm behaves like the old
// Matchade pane, the other attention states like Att granska, done like
// Klara.
function paneModeFor(displayState) {
    if (displayState === DISPLAY_TO_CONFIRM) return TIER_MATCH;
    if (ATTENTION_STATES.includes(displayState)) return TIER_REVIEW;
    return TIER_DONE;
}

// The row's state, one word. Which KIND of proposal (koppla vs bokför) is the
// body's job — its eyebrow says "Förslag" or "Redan bokförd?".
function StatusBadge({ line }) {
    const state = deriveDisplayState(line);
    const cfg = DISPLAY_CONFIG[state] || DISPLAY_CONFIG[DISPLAY_NEEDS_YOU];
    const Icon = cfg.icon;
    return (
        <span style={{ color: cfg.color, fontWeight: 500, fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
            <Icon size={13} />
            {line.reconciliation_exception === 'legacy_resolution_needs_review' ? 'Äldre avstämning – granska' : cfg.label}
        </span>
    );
}

// Parsed suggestion snapshot for a list row (string on the mysql driver,
// object on newer payloads). null when absent/broken.
function snapshotOf(line) {
    const raw = line?.suggestion_snapshot;
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(raw); } catch (e) { return null; }
}

// Categorical certainty pill for the list (§4.3 — tier, never a raw score).
const ROW_TIER = {
    high: { label: 'Hög', color: '#10b981', bg: '#ecfdf5' },
    medium: { label: 'Medel', color: '#b45309', bg: '#fffbeb' },
    low: { label: 'Låg', color: '#6b7280', bg: '#f3f4f6' },
};

/**
 * The row's match line — WHAT the engine knows about this row, shown in
 * the list itself instead of only in the detail pane (owner feedback
 * 2026-07-26): the snapshot label for suggestions ("Bokförd som V99 …",
 * "Regel: …"), the match target for engine matches, and an honest
 * "no suggestion" for the rest.
 */
function rowMatchInfo(line) {
    const state = deriveDisplayState(line);
    const snap = snapshotOf(line);
    switch (state) {
        case DISPLAY_SUGGESTED:
            return {
                text: (snap && snap.label) || strategyLabel(snap && snap.strategy) || 'Förslag finns',
                tier: snap && snap.tier,
                // Hover-preview payload (owner feedback 2026-07-26): the
                // provenance sentence + the proposed kontering rows.
                detail: (snap && snap.detail) || null,
                rows: (snap && Array.isArray(snap.rows)) ? snap.rows : [],
            };
        case DISPLAY_TO_CONFIRM:
            // Same verb family as the suggestions, tense carries the state:
            // 'Koppla till …' = proposal (nothing linked yet);
            // 'Kopplad till …' = the engine already made the link, the
            // click only confirms it.
            return {
                text: line.verification_id
                    ? `Kopplad till verifikat${line.verification_number ? ` ${line.verification_series || ''}${line.verification_number}` : ''}`
                    : (line.matched_invoice_id ? 'Kopplad till faktura' : 'Matchad automatiskt'),
            };
        case DISPLAY_NEEDS_YOU:
            return { text: 'Inget förslag — öppna för att bokföra' };
        case DISPLAY_BOOKED:
            return {
                text: line.verification_id
                    ? `Verifikat ${line.verification_series || ''}${line.verification_number || ''} kopplat`.replace('  ', ' ')
                    : 'Bekräftad som avstämd',
                // No human matcher on a booked row = the system did it
                // (rule auto-post) — say so.
                auto: Boolean(line.verification_id && !line.matched_by_user_id),
            };
        default:
            return null;
    }
}

// One statement line as a compact two-line row: transaction facts on top,
// the match story + status underneath. Hovering a suggested row opens a
// kontering preview popover — see the proposed booking without clicking
// (owner feedback 2026-07-26).
function StatementRow({ line, bankLeg, isSelected, isPending, disablePreview, onSelect }) {
    const amount = parseFloat(line.amount);
    const isInflow = amount > 0;
    const match = rowMatchInfo(line);
    const secondaryFacts = bankSecondaryFacts(line);
    // Hover preview shows the COMPLETE verifikat: the bank leg (mapped
    // konto, exact line amount) + the proposed counter rows.
    const previewRows = (match && match.rows && match.rows.length > 0 && bankLeg)
        ? [{
            account_number: bankLeg.account_number,
            account_name: bankLeg.account_name,
            debit: isInflow ? Math.abs(amount) : 0,
            credit: isInflow ? 0 : Math.abs(amount),
        }, ...match.rows]
        : (match ? match.rows : []);
    const tier = match && match.tier ? ROW_TIER[match.tier] : null;
    const hasPreview = Boolean(match && (match.detail || (match.rows && match.rows.length > 0)));

    const row = (
        <button
            type="button"
            onClick={onSelect}
            className="matching-list-item"
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderBottom: '1px solid #f3f4f6',
                borderLeft: isSelected ? '3px solid var(--primary)' : '3px solid transparent',
                background: isSelected ? 'var(--rgba-primary-1)' : 'transparent',
                padding: '10px 16px',
                cursor: 'pointer',
                // Loading feedback (owner feedback 2026-07-26): the clicked
                // row dims while its suggestions load — unmissable next to
                // the spinner.
                opacity: isPending ? 0.5 : 1,
                transition: 'opacity 0.15s ease',
            }}
            aria-busy={isPending || undefined}
        >
            {/* Row 1 — date + description + amount */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isInflow
                    ? <TrendingUp size={14} style={{ color: 'var(--bs-success)', flexShrink: 0 }} />
                    : <TrendingDown size={14} style={{ color: 'var(--bs-danger)', flexShrink: 0 }} />}
                <span style={{ fontWeight: 600, fontSize: 13, color: '#111827', whiteSpace: 'nowrap' }}>
                    {formatDate(bankTransactionDate(line))}
                </span>
                <span style={{ fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {bankTransactionLabel(line) || '(ingen beskrivning)'}
                </span>
                {/* Only facts the headline does not already say (type, named
                    counterparty) — never a "missing" placeholder in the list. */}
                {secondaryFacts.length > 0 && (
                    <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0, maxWidth: '35%' }}>
                        {secondaryFacts.map((fact) => fact.value).join(' · ')}
                    </span>
                )}
                <span style={{
                    marginLeft: 'auto',
                    fontSize: 13,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: isInflow ? 'var(--bs-success)' : 'var(--bs-danger)',
                }}>
                    {formatCurrency(amount)}
                </span>
            </div>
            {/* Row 2 — the match story + tier + status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                {match && (
                    <span style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {match.text}
                    </span>
                )}
                {match && match.auto && (
                    <span style={{
                        flexShrink: 0,
                        border: '1px solid #e5e7eb',
                        color: '#6b7280',
                        borderRadius: 999,
                        padding: '0 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: '18px',
                    }}
                    title="Bokförd automatiskt av en regel"
                    >
                        Auto
                    </span>
                )}
                {tier && (
                    <span style={{
                        flexShrink: 0,
                        background: tier.bg,
                        color: tier.color,
                        borderRadius: 999,
                        padding: '0 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: '18px',
                    }} title="Förslagets bedömningspoäng, inte en uppmätt sannolikhet.">
                        {tier.label}
                    </span>
                )}
                <span style={{ marginLeft: 'auto', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {isPending && <Spinner animation="border" size="sm" style={{ width: 14, height: 14, color: 'var(--primary)' }} />}
                    <StatusBadge line={line} />
                </span>
            </div>
        </button>
    );

    // No hover preview while the detail panel is open (the full answer is
    // already on screen) or while this row is loading.
    if (!hasPreview || disablePreview || isPending) return row;

    // Kontering preview on hover: the proposed booking + provenance,
    // without opening the detail pane.
    return (
        <OverlayTrigger
            placement="auto"
            delay={{ show: 350, hide: 0 }}
            overlay={(
                <Popover id={`row-preview-${line.id}`} className="bankrow-popover" style={{ maxWidth: 340 }}>
                    <Popover.Body>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#1f2937' }}>
                            {match.text}
                        </div>
                        {match.detail && match.detail !== match.text && (
                            <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 2 }}>
                                {match.detail}
                            </div>
                        )}
                        {previewRows && previewRows.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                {sortRowsByAccount(previewRows).map((r, i) => (
                                    <div
                                        key={`${r.account_number}-${i}`}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            gap: 8,
                                            padding: '4px 0',
                                            borderTop: i > 0 ? '1px dashed #e5e7eb' : 'none',
                                        }}
                                    >
                                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
                                            {r.account_number}
                                        </span>
                                        <span style={{ fontSize: '0.75rem', color: '#6b7280', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {r.account_name || ''}
                                        </span>
                                        <span style={{ fontSize: '0.75rem', color: '#374151', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                            {r.debit > 0 ? `D ${formatCurrency(r.debit)}` : `K ${formatCurrency(r.credit)}`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 6 }}>
                            Klicka på raden för att bekräfta eller ändra.
                        </div>
                    </Popover.Body>
                </Popover>
            )}
        >
            {row}
        </OverlayTrigger>
    );
}

/**
 * Per-strategy details block for a suggestion — structured Swedish
 * fields, never the backend's English matchReason (audit A3/B8).
 */
function suggestionDetails(suggestion) {
    const strategy = suggestion.matchStrategy || suggestion.strategy;
    const rows = [];
    const push = (label, value) => {
        if (value != null && value !== '') rows.push([label, value]);
    };
    const money = (value) => (
        value != null && value !== '' && Number.isFinite(parseFloat(value))
            ? formatCurrency(parseFloat(value))
            : null
    );

    switch (strategy) {
        case 'verification':
            push('Verifikat', suggestion.verificationNumber);
            push('Datum', formatDate(suggestion.verificationDate || suggestion.date));
            push('Beskrivning', suggestion.description);
            push('Belopp', money(suggestion.amount));
            break;
        case 'reference_exact':
        case 'reference_customer':
            push('Faktura', Array.isArray(suggestion.invoiceNumbers)
                ? suggestion.invoiceNumbers.join(' + ')
                : suggestion.invoiceNumber);
            push('Kund', suggestion.customerName);
            push('Belopp', money(suggestion.amount));
            push('Fakturadatum', suggestion.invoiceDate ? formatDate(suggestion.invoiceDate) : null);
            break;
        case 'origination_supplier_payment':
            push('Faktura', suggestion.invoiceNumber);
            push('Leverantör', suggestion.supplierName);
            push('Belopp', money(suggestion.amount));
            push('Betaldatum', suggestion.paymentDate ? formatDate(suggestion.paymentDate) : null);
            push('Verifikat', suggestion.verificationNumber);
            break;
        case 'origination_customer_payment':
            push('Faktura', suggestion.invoiceNumber);
            push('Kund', suggestion.customerName);
            push('Belopp', money(suggestion.amount));
            push('Betaldatum', suggestion.paymentDate ? formatDate(suggestion.paymentDate) : null);
            push('Verifikat', suggestion.verificationNumber);
            break;
        case 'origination_payroll':
            push('Utbetalningsdatum', suggestion.payDate ? formatDate(suggestion.payDate) : null);
            push('Belopp', money(suggestion.amount));
            break;
        case 'origination_tax_transfer':
            push('Skattekontorad', formatDate(suggestion.taxStatementDate));
            push('Belopp', money(suggestion.amount));
            break;
        case 'origination_payment_file':
            push('Betalfil', suggestion.paymentFile?.label);
            push('Betalningar', suggestion.paymentFile ? `${suggestion.paymentFile.row_count} av ${suggestion.paymentFile.total_rows_in_file}` : null);
            push('Belopp', money(suggestion.amount));
            if (suggestion.paymentFile?.link_count) push('Redan betalda', suggestion.paymentFile.link_count);
            break;
        case 'internal_transfer':
            push('Motkonto', suggestion.accountNumber);
            push('Datum', formatDate(suggestion.transactionDate));
            push('Beskrivning', suggestion.description);
            push('Belopp', money(suggestion.amount));
            break;
        default:
            push('Beskrivning', suggestion.description);
            push('Belopp', money(suggestion.amount));
            break;
    }
    return rows;
}

// Class-plausibility (and other) soft warnings — one amber line each,
// on any suggestion card that carries them. Guidance, never a block.
function SuggestionWarnings({ warnings }) {
    if (!Array.isArray(warnings) || warnings.length === 0) return null;
    return (
        <div className="mt-2">
            {warnings.map((warning) => (
                <div key={warning} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', color: '#b45309', fontSize: '0.75rem', marginTop: 2 }}>
                    <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>{warning}</span>
                </div>
            ))}
        </div>
    );
}

// Small uppercase section marker — the state of the row in one word,
// before any content. The eyebrow + verdict sentence replace the old
// "Matchningsförslag" jargon header (owner feedback 2026-07-26: the pane
// must be self-explanatory, never explained).
// Caret-less ⋮ trigger rendered as one of the sheet footer's icon actions
// (same pattern as the inbox sheet's ManageItemMenu).
const KebabToggle = React.forwardRef(function KebabToggle({ onClick, disabled }, ref) {
    return (
        <button
            ref={ref}
            type="button"
            className="ll-sheet-action"
            disabled={disabled}
            aria-label="Fler åtgärder"
            title="Fler åtgärder"
            onClick={(e) => { e.preventDefault(); onClick(e); }}
        >
            <MoreVertical size={16} />
        </button>
    );
});

function Eyebrow({ color = 'var(--primary)', children }) {
    return (
        <div style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color,
            marginBottom: 4,
        }}>
            {children}
        </div>
    );
}

// Retrieval-source labels for history suggestions (L3v2 blend). Older
// payloads omit retrievalSource entirely → no chip. Purely informational.
const RETRIEVAL_SOURCE_LABELS = {
    embedding: 'Likhetssökning',
    lexical: 'Textmatch',
    both: 'Likhetssökning + text',
};

// Small muted chip telling the user HOW a history precedent was found —
// only rendered when retrievalSource is present on the suggestion.
function RetrievalSourceChip({ source }) {
    const label = RETRIEVAL_SOURCE_LABELS[source];
    if (!label) return null;
    return (
        <span
            title="Hur det här förslaget hittades"
            style={{
                border: '1px solid #e5e7eb',
                background: '#f9fafb',
                color: '#6b7280',
                borderRadius: 999,
                padding: '1px 8px',
                fontSize: '0.6875rem',
                fontWeight: 500,
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </span>
    );
}

/**
 * History-layer suggestion card (§4.1 L3 — "bokförd som V-… tidigare").
 *
 * Two shapes:
 *  - precedent template: matchReason headline + neighbor chips + the
 *    scaled counter rows; "Bokför så här" prefills the booking wizard
 *    (an L3 suggestion proposes a BOOKING, not a match — never the
 *    confirm-suggestion endpoint).
 *  - group backoff (groupSuggestion): info-style guidance card only —
 *    kontogrupp hint and original historical rows, no confirm button.
 */
function HistorySuggestionCard({ suggestion, bankLeg, busy, onOpenNeighbor, guidanceOnly }) {
    const tier = confidenceTier(suggestion.confidence);

    if (suggestion.groupSuggestion) {
        // Guidance card — neutral gray like the priors evidence line (Phase E:
        // the old blue info box was off-palette; guidance is muted, not blue).
        return (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', marginBottom: 8, background: '#f9fafb' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <Info size={16} style={{ color: '#6b7280', flexShrink: 0, marginTop: 1 }} />
                    <div>
                        <div style={{ fontSize: '0.8125rem', color: '#374151', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span>
                                {suggestion.matchReason
                                    || `Liknande bokförs oftast i grupp ${suggestion.group}${suggestion.groupName ? ` – ${suggestion.groupName}` : ''}`}
                            </span>
                            <RetrievalSourceChip source={suggestion.retrievalSource} />
                        </div>
                        {suggestion.usageCount > 0 && (
                            <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 2 }}>
                                Baserat på {suggestion.usageCount} liknande bokningar — vägledning, inget förslag att bekräfta.
                            </div>
                        )}
                        <HistoricalBookingEvidence neighbors={suggestion.neighborVerifications} busy={busy} onOpen={onOpenNeighbor} />
                    </div>
                </div>
                <SuggestionWarnings warnings={suggestion.warnings} />
            </div>
        );
    }

    const usage = suggestion.usageCount || 0;
    const top = (suggestion.neighborVerifications || [])[0];

    return (
        <div style={{ marginBottom: 28 }}>
            <Eyebrow>Förslag</Eyebrow>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.875rem', color: '#1f2937' }}>
                    Bokför så här — så bokförde du liknande transaktioner tidigare.
                </span>
                <Badge bg={tier.variant} style={{ fontSize: '0.6875rem' }} title="Förslagets bedömningspoäng, inte en uppmätt sannolikhet.">
                    {confidenceLabel(suggestion.confidence)}
                </Badge>
            </div>

            {/* The COMPLETE verifikat incl. the bank leg from the account
                mapping (owner feedback 2026-07-26) — the slip shows exactly
                what booking will create, balanced. */}
            <KonteringSlip rows={
                buildHistoryPrefillRows(suggestion, bankLeg)
                || normalizeSuggestionRows(suggestion.rows)
            } />

            {/* Provenance in small print — evidence, not headline. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <span style={{ fontSize: '0.6875rem', color: '#9ca3af' }}>
                    {usage > 0
                        ? `Baserat på ${usage} tidigare ${usage === 1 ? 'bokföring' : 'bokföringar'}${top ? `, senast ${top.number || formatDate(top.date)}` : ''}`
                        : 'Baserat på din bokföringshistorik'}
                </span>
                <RetrievalSourceChip source={suggestion.retrievalSource} />
            </div>
            <SuggestionWarnings warnings={suggestion.warnings} />

            {guidanceOnly && (
                // A booked verifikat already matches this line — booking again
                // would duplicate it. Show the history purely as context
                // ("so this is booked", above) with no booking action.
                <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.75rem' }}>
                    Så här brukar du bokföra liknande — koppla till det bokförda verifikatet ovan i stället för att bokföra på nytt.
                </p>
            )}

            {/* Evidence supports the decision; it sits under the action, not between proposal and action. */}
            <HistoricalBookingEvidence neighbors={suggestion.neighborVerifications} busy={busy} onOpen={onOpenNeighbor} />
        </div>
    );
}

/**
 * AI-förslag card — the entitlement-gated LLM fallback. The LLM proposes
 * a BOOKING (rows + Swedish rationale), so its confirm prefills the
 * booking wizard for review — never the confirm-suggestion endpoint.
 * Amber review styling: an AI answer is always granska-först.
 */
function LlmSuggestionCard({ suggestion }) {
    const rows = normalizeSuggestionRows(suggestion.rows);
    const rationale = suggestion.rationale || suggestion.motivation || suggestion.reasoning || suggestion.matchReason;
    return (
        <div style={{ marginBottom: 28 }}>
            <Eyebrow color="#b45309">
                <Sparkles size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
                AI-förslag — granska innan du bokför
            </Eyebrow>
            {rationale && (
                <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: 0 }}>
                    {rationale}
                </p>
            )}
            <KonteringSlip rows={rows} />
            <SuggestionWarnings warnings={suggestion.warnings} />
        </div>
    );
}

/**
 * Plain-language verdict per strategy (owner feedback 2026-07-26): the
 * pane states the situation in one sentence a first-time user parses
 * without help. 'Koppla' verdicts always carry the no-double-booking
 * reassurance — the exact fear the Bokio anti-pattern taught us to
 * pre-empt.
 */
function verdictFor(suggestion) {
    const strategy = suggestion.matchStrategy || suggestion.strategy;
    switch (strategy) {
        case 'verification':
            return {
                eyebrow: 'Redan bokförd?',
                sentence: `Raden ser ut att motsvara ${suggestion.verificationNumber ? `verifikat ${suggestion.verificationNumber}` : 'ett verifikat'} som redan finns i din bokföring.`,
                action: 'Koppla till verifikatet',
                note: 'Inget nytt bokförs — raden kopplas ihop med det befintliga verifikatet.',
            };
        case 'reference_exact':
        case 'reference_customer': {
            const invoice = Array.isArray(suggestion.invoiceNumbers)
                ? suggestion.invoiceNumbers.join(' + ')
                : suggestion.invoiceNumber;
            return {
                eyebrow: 'Fakturabetalning',
                sentence: `Raden ser ut som betalningen av ${invoice ? `faktura ${invoice}` : 'en kundfaktura'}.`,
                action: 'Koppla till fakturan',
                note: 'Fakturan markeras som betald och raden kopplas till den.',
            };
        }
        case 'origination_payroll':
            return {
                eyebrow: 'Egen utbetalning',
                sentence: 'Raden matchar en lönekörning du gjort i LedgerLoop.',
                action: 'Koppla till lönekörningen',
                note: 'Inget nytt bokförs — raden kopplas till lönekörningen.',
            };
        case 'origination_supplier_payment':
            return {
                eyebrow: 'Egen utbetalning',
                sentence: 'Raden matchar en leverantörsbetalning du registrerat.',
                action: 'Koppla till betalningen',
                note: `Inget nytt bokförs — raden kopplas till betalningen${suggestion.verificationNumber ? ` och dess verifikat ${suggestion.verificationNumber}` : ''}.`,
            };
        case 'origination_customer_payment':
            return {
                eyebrow: 'Registrerad betalning',
                sentence: `Raden matchar en betalning du registrerat${suggestion.invoiceNumber ? ` på faktura ${suggestion.invoiceNumber}` : ''}.`,
                action: 'Koppla till betalningen',
                note: `Inget nytt bokförs — raden kopplas till betalningen${suggestion.verificationNumber ? ` och dess verifikat ${suggestion.verificationNumber}` : ''}.`,
            };
        case 'origination_payment_file': {
            const pf = suggestion.paymentFile || {};
            const creates = pf.create_count || 0;
            const salary = pf.category_purpose === 'SALA';
            return {
                eyebrow: 'Egen betalfil',
                sentence: `Raden ser ut som debiteringen av betalfilen ${pf.label || ''} (${pf.row_count || '?'} av ${pf.total_rows_in_file || '?'} betalningar)${suggestion.matchReasonSv ? ` — ${suggestion.matchReasonSv.replace(/^Betalfil [^:]*: /, '')}` : ''}`,
                action: salary ? 'Koppla till lönekörningen' : creates ? 'Bokför betalningarna' : 'Koppla till betalfilen',
                note: salary
                    ? 'Inget nytt bokförs — lönekörningens verifikat kopplas och raderna i filen markeras som betalda.'
                    : creates
                        ? `${creates} faktur${creates === 1 ? 'a' : 'or'} bokförs som betald${creates === 1 ? '' : 'a'} mot bankkontot${pf.link_count ? `; ${pf.link_count} redan registrerad${pf.link_count === 1 ? '' : 'e'} betalning${pf.link_count === 1 ? '' : 'ar'} kopplas` : ''}.`
                        : 'Inget nytt bokförs — de registrerade betalningarna kopplas till bankhändelsen.',
            };
        }
        case 'origination_tax_transfer':
            return {
                eyebrow: 'Skattekonto',
                sentence: 'Raden ser ut som en överföring till ditt skattekonto.',
                action: 'Bekräfta',
                note: null,
            };
        case 'internal_transfer':
            return {
                eyebrow: 'Egen överföring',
                sentence: 'Raden ser ut som en överföring mellan dina egna konton.',
                action: 'Bokför överföring',
                note: null,
            };
        case 'rule':
            return {
                eyebrow: 'Regel',
                sentence: suggestion.ruleName
                    ? `Regeln "${suggestion.ruleName}" känner igen den här transaktionen.`
                    : 'En av dina regler känner igen den här transaktionen.',
                action: 'Bokför enligt regeln',
                note: null,
            };
        default:
            return {
                eyebrow: 'Förslag',
                sentence: strategyLabel(strategy),
                action: 'Bekräfta',
                note: null,
            };
    }
}

const INVOICE_STRATEGIES = ['supplier_invoice', 'reference_exact', 'reference_customer'];
const strategyOf = (suggestion) => suggestion?.matchStrategy || suggestion?.strategy;

// Why the engine thinks this is the invoice, in a few words.
function invoiceEvidence(suggestion) {
    const strategy = strategyOf(suggestion);
    if (strategy === 'reference_exact') return 'Referens och belopp stämmer';
    if (strategy === 'reference_customer') return 'Referensen pekar ut kunden, beloppet stämmer';
    if (suggestion.evidence === 'reference') return 'Referens och belopp stämmer';
    if (suggestion.evidence === 'name') return 'Leverantör och belopp stämmer';
    return 'Beloppet stämmer';
}

// Under the kontering of an invoice payment: which earlier bookings it was
// taken from, and the other ways the same supplier has been booked. One
// line per kontering: its accounts, and how often it was used.
function PaymentKonteringBasis({ proposals, chosen, onChoose, busy, onOpenNeighbor }) {
    const current = proposals[chosen] || proposals[0];
    const accounts = (proposal) => normalizeSuggestionRows(proposal.rows)
        .map((row) => `${row.account_number}${row.account_name ? ` ${row.account_name}` : ''}`)
        .join(' · ');
    const used = (proposal) => {
        const count = proposal.usageCount || 0;
        const top = (proposal.neighborVerifications || [])[0];
        const latest = top ? `, senast ${top.number || formatDate(top.date)}` : '';
        return count > 0 ? `${count} ${count === 1 ? 'gång' : 'gånger'} tidigare${latest}` : 'tidigare bokföring';
    };
    return (
        <div style={{ marginTop: 18 }}>
            <Eyebrow color="#6b7280">Så har du bokfört det tidigare</Eyebrow>
            {proposals.length > 1 ? (
                <div role="radiogroup" aria-label="Tidigare konteringar">
                    {proposals.map((proposal, index) => (
                        <label key={index} style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: 0, padding: '4px 0', fontSize: '0.8125rem', color: '#111827', cursor: 'pointer' }}>
                            <input type="radio" name="payment-kontering" checked={index === chosen} onChange={() => onChoose(index)} style={{ accentColor: 'var(--primary)', flexShrink: 0 }} />
                            <span style={{ flex: 1, minWidth: 0 }}>{accounts(proposal)}</span>
                            <span style={{ flexShrink: 0, fontSize: '0.75rem', color: '#6b7280' }}>{used(proposal)}</span>
                            <RetrievalSourceChip source={proposal.retrievalSource} />
                        </label>
                    ))}
                </div>
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: '0.8125rem', color: '#374151' }}>
                    <span>Bokfört så {used(current)}.</span>
                    <RetrievalSourceChip source={current.retrievalSource} />
                </div>
            )}
            <HistoricalBookingEvidence neighbors={current.neighborVerifications} busy={busy} onOpen={onOpenNeighbor} />
            <SuggestionWarnings warnings={current.warnings} />
        </div>
    );
}

// An invoice the bank row may have paid, as one scannable block (owner
// feedback 2026-09-29: the sentence cards were too much to read): who and
// how much on the first line, which invoice on the second, why on the third.
// Several invoices with the same amount are shown together as a choice, under
// one heading; `eyebrow` is then set on the first card only.
function InvoiceMatchCard({ suggestion, eyebrow, grouped = false, hideWarnings = false }) {
    const supplier = strategyOf(suggestion) === 'supplier_invoice';
    const counterparty = (supplier ? suggestion.supplierName : suggestion.customerName) || (supplier ? 'Leverantör' : 'Kund');
    const invoiceNumber = Array.isArray(suggestion.invoiceNumbers) ? suggestion.invoiceNumbers.join(' + ') : suggestion.invoiceNumber;
    const today = new Date().toISOString().slice(0, 10);
    const facts = [
        invoiceNumber ? `Faktura ${invoiceNumber}` : null,
        suggestion.invoiceDate ? formatDate(suggestion.invoiceDate) : null,
        suggestion.dueDate ? `${formatDate(suggestion.dueDate) < today ? 'förföll' : 'förfaller'} ${formatDate(suggestion.dueDate)}` : null,
    ].filter(Boolean);
    const amount = parseFloat(suggestion.amount);
    return (
        <div style={{ marginBottom: grouped ? 14 : 24 }}>
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {counterparty}
                </span>
                {Number.isFinite(amount) && (
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {formatCurrency(amount)}
                    </span>
                )}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                {facts.join(' · ')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 12, fontSize: '0.75rem', marginTop: 6 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#047857' }}>
                    <CheckCircle size={12} /> {invoiceEvidence(suggestion)}
                </span>
                {suggestion.needsApproval && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#b45309' }}>
                        <AlertTriangle size={12} /> Inte godkänd ännu
                    </span>
                )}
            </div>
            {!hideWarnings && <SuggestionWarnings warnings={suggestion.warnings} />}
        </div>
    );
}

// Deterministic-match card: verdict sentence first, a compact fact line,
// ONE primary action. No jargon headers, no data tables.
function InlineSuggestionCard({ suggestion, guidanceOnly, bankLeg, companyId }) {
    const tier = confidenceTier(suggestion.confidence);
    const transfer = isTransferSuggestion(suggestion);
    const confirmTargets = buildConfirmTargets(suggestion);
    const confirmable = confirmTargets.length > 0;
    const verdict = verdictFor(suggestion);
    const details = suggestionDetails(suggestion);
    // One-step (owner follow-up 2026-07-26): confirming an invoice match
    // also registers the payment — one action, plain sentence, with
    // "endast koppla" as the escape hatch.
    const invoiceMatch = confirmable
        && confirmTargets.every((t) => t.target_type === 'customer_invoice');

    // "Redan bokförd?" answers itself (owner 2026-07-27): verifikat
    // proposals load the target verifikat and show its kontering inline.
    const [targetVerification, setTargetVerification] = useState(null);
    const strategyKey = suggestion.matchStrategy || suggestion.strategy;
    useEffect(() => {
        setTargetVerification(null);
        if (strategyKey !== 'verification' || !suggestion.candidateId) return undefined;
        let cancelled = false;
        getVerificationById(companyId, suggestion.candidateId)
            .then((data) => {
                if (!cancelled) setTargetVerification(data?.verification || data || null);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [strategyKey, suggestion.candidateId, companyId]);
    // A booked verifikat already matches this line — a transfer that BOOKS a
    // new verifikat would duplicate it (walkthrough finding 2026-07-20).
    // Confirm-type suggestions (link only) are always safe.
    const suppressTransferBooking = transfer && guidanceOnly;

    return (
        <div style={{ marginBottom: 28 }}>
            <Eyebrow>{verdict.eyebrow}</Eyebrow>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.875rem', color: '#1f2937' }}>
                    {verdict.sentence}
                </span>
                <Badge bg={tier.variant} style={{ fontSize: '0.6875rem' }} title="Förslagets bedömningspoäng, inte en uppmätt sannolikhet.">
                    {confidenceLabel(suggestion.confidence)}
                </Badge>
            </div>

            {details.length > 0 && (
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 10 }}>
                    {details.map(([label, value]) => `${label}: ${value}`).join(' · ')}
                </div>
            )}

            <SuggestionWarnings warnings={suggestion.warnings} />

            {targetVerification && (
                <KonteringSlip rows={(targetVerification.transactions || []).map((t) => ({
                    account_number: t.account_number,
                    account_name: t.account_name,
                    debit: t.transaction_type === 'debit' ? t.amount : 0,
                    credit: t.transaction_type === 'credit' ? t.amount : 0,
                }))} />
            )}

            {suppressTransferBooking ? (
                <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.75rem' }}>
                    Ett bokfört verifikat matchar redan raden (ovan) — koppla till det i stället för att bokföra överföringen på nytt.
                </p>
            ) : (
                <>
                    {/* What the footer's primary button will do, in small print. */}
                    {invoiceMatch ? (
                        <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 10 }}>
                            Fakturan markeras som betald och betalningen bokförs på
                            {' '}{(bankLeg && bankLeg.account_number) || 'företagskontot (rollen är inte mappad)'} med radens datum.
                        </div>
                    ) : verdict.note ? (
                        <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 10 }}>
                            {verdict.note}
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}

/**
 * Global-priors evidence line (§4.6b, Phase 4 item 3) — the cross-tenant
 * "{percent} av N företag bokför liknande som …" advisory. Rendered ONLY when
 * the line has no strong own suggestion. It is EVIDENCE, never authority: a
 * muted informational line, never a confirm button, never overriding a
 * suggestion. Renders nothing when there is no safe prior.
 */
function PriorsEvidenceLine({ prior }) {
    const text = priorsEvidenceText(prior);
    if (!text) return null;
    return (
        <div
            style={{
                display: 'flex', gap: 8, alignItems: 'flex-start',
                border: '1px solid #e5e7eb', background: '#f9fafb', borderRadius: 8,
                padding: '10px 12px', margin: '20px 0',
            }}
        >
            <Users size={14} style={{ color: '#6b7280', flexShrink: 0, marginTop: 2 }} />
            <div>
                <div style={{ fontSize: '0.8125rem', color: '#4b5563' }}>{text}</div>
                <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 2 }}>
                    Vägledning från andra företag — inget att bekräfta, dina egna regler och tidigare bokföringar går alltid först.
                </div>
            </div>
        </div>
    );
}

// The one decision a suggestion asks for, as the sheet footer's primary
// button: label, icon and which handler runs it. null = context only (a
// guidance card, or a booking that would duplicate an already-booked verifikat).
function primaryActionFor(suggestion, guidanceOnly) {
    if (!suggestion || suggestion.groupSuggestion) return null;
    const strategy = suggestion.matchStrategy || suggestion.strategy;
    if (strategy === 'history') {
        return guidanceOnly ? null : { kind: 'prefill', label: 'Bokför så här', icon: FilePlus };
    }
    const verdict = verdictFor(suggestion);
    if (isTransferSuggestion(suggestion)) {
        return guidanceOnly ? null : { kind: 'transfer', label: verdict.action, icon: FilePlus };
    }
    const targets = buildConfirmTargets(suggestion);
    const confirmable = targets.length > 0;
    // An invoice that is entered but not approved cannot take a payment yet.
    if (strategy === 'supplier_invoice' && suggestion.needsApproval) {
        return { kind: 'openInvoice', label: 'Öppna fakturan för att godkänna', icon: FileText };
    }
    // An unpaid invoice, customer or supplier: confirming registers the
    // payment. A payment that is already registered (the suggestion names
    // it) only links, and says so.
    const registered = !!(suggestion.paymentId || suggestion.payment_id);
    if (confirmable && !registered && (targets.every((t) => t.target_type === 'customer_invoice') || strategy === 'supplier_invoice')) {
        return { kind: 'confirmPayment', label: 'Koppla och registrera betalning', icon: CheckCircle };
    }
    // A payment-file suggestion names its rows; the settle action is the confirm.
    const fileRows = strategy === 'origination_payment_file' && Array.isArray(suggestion.instructionIds) && suggestion.instructionIds.length > 0;
    return { kind: 'confirm', label: verdict.action, icon: CheckCircle, disabled: !confirmable && !fileRows };
}

// Right-pane detail for the selected line, tier-aware (audit D5/A7):
// Att granska gets inline suggestions; the decision itself lives in the
// footer (primary + Avvisa). Matchade gets bekräfta in the footer; Klara is
// read-only with Visa verifikat, undo behind the ⋮ menu.
function StatementDetail({
    line,
    bankLeg,
    companyId,
    tier,
    onClose,
    onPrev,
    onNext,
    position,
    bank,
    suggestions,
    suggestionsLoading,
    suggestionsError,
    actionBusy,
    onConfirmSuggestion,
    onBookTransfer,
    onRejectSuggestion,
    onRestoreRejections,
    rejectedCount,
    onPrefillHistoryBooking,
    onOpenNeighbor,
    prior,
    llm,
    onAskAi,
    onUseLlmSuggestion,
    onCreateVerification,
    onMarkReconciledExternal,
    onConfirmSingle,
    onReset,
    onShowVerification,
    linkedVerification,
    onAskAssistant,
    // Slots from ReconciliationWorkspace (undefined when the backend has no
    // reconciliation contract): { agent, manualPaths, record, linking, notice }.
    reconciliation,
    // Inline booking view: null, or { token, initialRows?, manual? }.
    booking,
    onBookingBack,
    onBookingSuccess,
}) {
    const amount = parseFloat(line.amount);
    const isInflow = amount > 0;
    const status = line.reconciliation_status;
    // "Ångra" is legal for the four resettable statuses (D4/A7) — plus
    // batch-confirmed lines (status verified WITHOUT a verifikat), so the
    // batch dialog's undo promise stays true. Lines with a real verifikat
    // are never resettable from here.
    const canReset = RESETTABLE_STATUSES.includes(status)
        || (status === 'verified' && !line.verification_id);

    // ── Inline booking view ──────────────────────────────────────────────
    // `booking` swaps the body for the BookingEditor and the footer for
    // Bokför / Tillbaka. Both hooks stay idle until a booking opens. On a
    // bank outflow with the reconciliation contract, booking goes
    // prepare → preview → execute (usePurchaseAction); otherwise the
    // legacy POST (useBookingDraft.submit).
    const draft = useBookingDraft({ companyId, transaction: line, bankLeg, enabled: !!booking });
    const purchase = usePurchaseAction({
        companyId, line, rows: draft.rows, description: draft.description, enabled: !!booking && !!reconciliation,
        payment: booking?.payment || null,
    });
    const bookingToken = booking?.token;
    // The payment of an invoice: complete konteringar from earlier bookings.
    // The first one is in the rows when the view opens; choosing another
    // replaces the rows.
    const paymentProposals = booking?.payment ? (booking.proposals || []) : [];
    const [chosenProposal, setChosenProposal] = useState(0);
    const chooseProposal = (index) => {
        const rows = buildHistoryPrefillRows(paymentProposals[index], bankLeg);
        if (!rows) return;
        setChosenProposal(index);
        draft.setRows(sortRowsByAccount(rows));
    };
    useEffect(() => {
        if (!bookingToken) return;
        draft.init({
            initialRows: booking.initialRows || null,
            manual: !!booking.manual,
            description: booking.payment
                ? [booking.payment.counterparty, booking.payment.invoiceNumber && `faktura ${booking.payment.invoiceNumber}`].filter(Boolean).join(', ')
                : null,
        });
        setChosenProposal(0);
        // A new token means "the view just opened"; nothing else re-inits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingToken]);
    const purchaseResult = purchase.action.result;
    useEffect(() => {
        if (booking && purchaseResult) onBookingSuccess?.(purchaseResult, booking);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [purchaseResult]);
    const bookingPreview = !!purchase.action.plan;
    const bookBusy = draft.creating || purchase.action.busy;
    const bookDisabled = bookBusy || purchase.checking || purchase.action.unknown || (!bookingPreview && !draft.canSubmit);
    const onBook = async () => {
        if (purchase.applies) {
            if (bookingPreview) await purchase.action.execute();
            else await purchase.prepare();
            return;
        }
        const data = await draft.submit();
        if (data) onBookingSuccess?.(data, booking);
    };
    const onBookingBackClick = () => {
        if (bookingPreview) purchase.action.clear();
        else onBookingBack?.();
    };

    // One primary suggestion, alternatives on demand (owner feedback
    // 2026-07-26: a booked-verifikat match next to history cards read as
    // noise). The ladder already ranks — the first suggestion IS the
    // answer; the rest collapse behind "Visa fler förslag".
    // Exception: open supplier invoices with the same amount are one question
    // ("which invoice did this pay?"), so they are shown together.
    const [showAllSuggestions, setShowAllSuggestions] = useState(false);
    useEffect(() => { setShowAllSuggestions(false); }, [line.id]);
    const invoiceChoice = strategyOf(suggestions[0]) === 'supplier_invoice'
        ? suggestions.filter((s) => strategyOf(s) === 'supplier_invoice')
        : [];
    const orderedSuggestions = invoiceChoice.length > 1
        ? [...invoiceChoice, ...suggestions.filter((s) => !invoiceChoice.includes(s))]
        : suggestions;
    const visibleSuggestions = showAllSuggestions ? orderedSuggestions : orderedSuggestions.slice(0, Math.max(1, invoiceChoice.length));
    const hiddenCount = suggestions.length - visibleSuggestions.length;
    const routerHistory = useHistory();

    // The decision lives in the footer, so the body needs exactly one
    // selected proposal. The top pick is selected by default; once the
    // alternatives are expanded, clicking a card selects it.
    const hasVerificationMatch = suggestions.some((s) => (s.matchStrategy || s.strategy) === 'verification');
    const cards = visibleSuggestions.map((suggestion, index) => ({
        key: `${suggestion.matchStrategy || suggestion.strategy || 's'}-${suggestion.candidateId || index}`,
        suggestion,
        action: primaryActionFor(suggestion, hasVerificationMatch),
    }));
    const showLlmCard = !suggestionsLoading && !suggestionsError && !!(llm && llm.suggestion);
    if (showLlmCard) {
        cards.push({
            key: 'llm',
            llm: true,
            action: { kind: 'llm', label: 'Använd förslaget', icon: FilePlus, disabled: normalizeSuggestionRows(llm.suggestion.rows).length < 2 },
        });
    }
    const selectable = cards.filter((c) => c.action);
    const [selectedKey, setSelectedKey] = useState(null);
    const cardKeys = cards.map((c) => c.key).join('|');
    useEffect(() => { setSelectedKey(null); }, [line.id, cardKeys]);
    const selected = selectable.find((c) => c.key === selectedKey) || selectable[0] || null;
    const runPrimary = () => {
        if (!selected) return undefined;
        switch (selected.action.kind) {
            case 'prefill': return onPrefillHistoryBooking(selected.suggestion);
            case 'transfer': return onBookTransfer(selected.suggestion);
            case 'confirmPayment': return onConfirmSuggestion(selected.suggestion, { registerPayment: true });
            case 'confirm': return onConfirmSuggestion(selected.suggestion);
            case 'openInvoice': return routerHistory.push(`/${companyId}/supplier-invoices/${selected.suggestion.candidateId}`);
            case 'llm': return onUseLlmSuggestion(llm.suggestion);
            default: return undefined;
        }
    };
    // A card becomes clickable only when there is something to choose between.
    const selectableCard = (card, node) => {
        if (selectable.length < 2 || !card.action) return <React.Fragment key={card.key}>{node}</React.Fragment>;
        const isSelected = selected?.key === card.key;
        const select = () => setSelectedKey(card.key);
        return (
            <div
                key={card.key}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                onClick={select}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(); } }}
                style={{
                    border: `1px solid ${isSelected ? 'var(--primary)' : '#e5e7eb'}`,
                    borderRadius: 10,
                    padding: '14px 16px 0',
                    marginBottom: 12,
                    cursor: 'pointer',
                }}
            >
                {node}
            </div>
        );
    };

    // Footer ⋮ menu: the escape hatches for the tier, plus the reconciliation
    // fallbacks on rows under review.
    const menuItems = [];
    if (tier === TIER_REVIEW) {
        menuItems.push({ key: 'create', icon: FilePlus, label: 'Skapa verifikat manuellt', onClick: onCreateVerification });
        menuItems.push({ key: 'external', icon: CheckCircle2, label: 'Markera som avstämd externt', onClick: onMarkReconciledExternal });
        if (reconciliation?.manualPaths?.length > 0) {
            menuItems.push({ key: 'divider' }, ...reconciliation.manualPaths);
        }
    } else if (tier === TIER_MATCH) {
        menuItems.push({ key: 'reset', icon: Undo2, label: 'Ångra matchning', onClick: onReset });
    } else if (tier === TIER_DONE && canReset) {
        menuItems.push({ key: 'reset', icon: Undo2, label: 'Ångra', onClick: onReset });
    }
    // AI entry points, last and behind a divider. The one-shot kontering
    // proposal (§4.1 L4) is offered only when the ladder came up empty or
    // weak and hidden entirely when the endpoint does not exist. The
    // assistant hand-off passes a descriptor built from `line.id`, never
    // from the bank-authored text.
    const aiItems = [];
    if (tier === TIER_REVIEW && !suggestionsLoading && !suggestionsError && llm
        && !llm.suggestion && !llm.unavailable && !hasStrongSuggestion(suggestions)) {
        aiItems.push({
            key: 'ask-ai',
            icon: Sparkles,
            label: llm.loading ? 'Frågar AI…' : llm.denied ? 'Föreslå kontering med AI (AI-krediter saknas)' : 'Föreslå kontering med AI',
            disabled: llm.loading || llm.denied,
            onClick: onAskAi,
        });
    }
    if (onAskAssistant) {
        aiItems.push({ key: 'assistant', icon: Sparkles, label: 'Öppna i assistenten', onClick: () => onAskAssistant(line) });
    }
    if (aiItems.length > 0) {
        if (menuItems.length > 0) menuItems.push({ key: 'divider-ai' });
        menuItems.push(...aiItems);
    }

    // The bank's bookkeeping facts (dates, reference, account, balance) live
    // behind an info icon next to the date (owner feedback 2026-09-28: the
    // meta line under the amount repeated the headline and facts above it).
    // The header date is the booking date; the other two only earn a mention
    // when they differ from it, and the reference only when it is not the
    // headline already.
    const headerDate = formatDate(bankTransactionDate(line));
    const otherDate = (value) => (value && formatDate(value) !== headerDate ? formatDate(value) : null);
    const headline = bankTransactionLabel(line).trim().toLowerCase();
    const bankFacts = [
        ['Bokföringsdatum', headerDate],
        ['Transaktionsdatum', otherDate(line.transaction_date)],
        ['Valutadatum', otherDate(line.value_date)],
        ['Referens', line.reference && String(line.reference).trim().toLowerCase() !== headline ? line.reference : null],
        ['Bankkonto', line.account_number || null],
        ['Bokförs på', bankLeg ? `${bankLeg.account_number}${bankLeg.account_name ? ` ${bankLeg.account_name}` : ''}` : null],
        ['Saldo efter', line.balance != null && line.balance !== '' ? formatCurrency(parseFloat(line.balance)) : null],
    ].filter(([, value]) => value);

    const textSources = [...new Set(Object.values(line.text_sources || {}).filter(Boolean))];
    const sourceLabel = textSources.length
        ? textSources.map(bankTextSourceLabel).filter(Boolean).join(' + ')
        : IMPORT_SOURCE_LABELS[line.source] || null;

    return (
        // Content of the page's <SideSheet> — the sheet is the frame; this
        // fills it with a fixed header zone and a scrolling body.
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {/* Fixed header zone: the transaction itself. */}
            <div style={{ padding: '16px 24px 22px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
                {/* Top row: where the row came from (bank logo from the feed
                    provider's bank list, Landmark fallback, + the import
                    rail) on the left, the date on the right. */}
                <div className="d-flex align-items-center justify-content-between" style={{ gap: 12, marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        <BankLogo bank={bank || {}} />
                        {(bank || sourceLabel) && (
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {bank ? bank.name : sourceLabel}
                                </div>
                                {bank && sourceLabel && (
                                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{sourceLabel}</div>
                                )}
                            </div>
                        )}
                    </div>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <span style={{ fontSize: '0.8125rem', color: '#6b7280', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }} title="Bokföringsdatum">
                            {headerDate}
                        </span>
                        {bankFacts.length > 1 && (
                            <OverlayTrigger
                                placement="bottom-end"
                                trigger={['hover', 'focus']}
                                overlay={(
                                    <Popover id={`bank-facts-${line.id}`} style={{ maxWidth: 320 }}>
                                        <Popover.Body style={{ padding: '10px 12px' }}>
                                            <dl style={{ margin: 0, fontSize: '0.75rem', color: '#374151', display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '3px 12px' }}>
                                                {bankFacts.map(([label, value]) => (
                                                    <React.Fragment key={label}>
                                                        <dt style={{ fontWeight: 500, color: '#6b7280', whiteSpace: 'nowrap' }}>{label}</dt>
                                                        <dd style={{ margin: 0, overflowWrap: 'anywhere', fontVariantNumeric: 'tabular-nums' }}>{value}</dd>
                                                    </React.Fragment>
                                                ))}
                                            </dl>
                                        </Popover.Body>
                                    </Popover>
                                )}
                            >
                                <button
                                    type="button"
                                    aria-label="Bankens uppgifter"
                                    style={{ display: 'inline-flex', padding: 0, border: 0, background: 'none', color: '#9ca3af', cursor: 'default' }}
                                >
                                    <Info size={14} />
                                </button>
                            </OverlayTrigger>
                        )}
                    </span>
                </div>
                <BankTransactionFacts line={line} />
                {/* The amount alone. The row's state belongs to the list; in
                    the sheet the body's eyebrow (Förslag / Redan bokförd /
                    Klar) says it one line down. */}
                <div style={{ marginTop: 4 }}>
                    <span style={{ fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.01em', color: isInflow ? '#10b981' : '#dc2626', fontVariantNumeric: 'tabular-nums' }}>
                        {formatCurrency(amount)}
                    </span>
                </div>
            </div>

            {/* Scrolling body zone: the verdict + actions. The key retriggers
                the entrance animation on every row swap / data arrival, so
                content changes read as motion, not a flash. */}
            <div
                key={`${line.id}:${suggestionsLoading ? 'loading' : 'ready'}:${booking ? 'book' : 'review'}`}
                className="bank-pane-in ll-sheet-body"
                style={{ padding: '24px 24px 28px', flex: 1, minHeight: 0 }}
            >
                {/* ── Bokför: the inline booking view ─────────────────── */}
                {booking && (
                    <>
                        <Eyebrow>{bookingPreview ? 'Förhandsgranskning' : booking.payment ? 'Bokför betalningen' : 'Bokför'}</Eyebrow>
                        {booking.payment && !bookingPreview && (
                            <div style={{ fontSize: '0.875rem', color: '#111827', marginBottom: 16 }}>
                                <strong style={{ fontWeight: 600 }}>{booking.payment.counterparty}</strong>
                                {booking.payment.invoiceNumber && (
                                    <span style={{ color: '#6b7280' }}> · faktura {booking.payment.invoiceNumber}</span>
                                )}
                            </div>
                        )}
                        <BookingEditor
                            key={bookingToken}
                            companyId={companyId}
                            draft={draft}
                            purchase={purchase}
                            templatesFirst={!!booking.payment && paymentProposals.length === 0}
                        />
                        {/* Where the kontering above comes from, as evidence under it. */}
                        {paymentProposals.length > 0 && !bookingPreview && (
                            <PaymentKonteringBasis
                                proposals={paymentProposals}
                                chosen={chosenProposal}
                                onChoose={chooseProposal}
                                busy={actionBusy}
                                onOpenNeighbor={onOpenNeighbor}
                            />
                        )}
                    </>
                )}

                {/* Why a link was refused, in the sheet where the click happened. */}
                {!booking && reconciliation?.notice}

                {!booking && line.reconciliation_exception === 'legacy_resolution_needs_review' && (
                    <p style={{ fontSize: '0.8125rem', color: '#b45309', marginBottom: 18 }}>
                        Raden stämdes av i ett äldre flöde och betalningen eller bokföringen kunde inte styrkas. Kontrollera och stäm av igen.
                    </p>
                )}

                {/* ── Att granska: the verdict leads — no jargon header ──── */}
                {!booking && tier === TIER_REVIEW && (
                    <>
                        {suggestionsLoading && (
                            <div style={{ padding: '12px 0', color: '#6b7280', fontSize: '0.875rem' }}>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Söker efter matchningar…
                            </div>
                        )}
                        {!suggestionsLoading && suggestionsError && (
                            <Alert variant="warning" className="py-2" style={{ fontSize: '0.8125rem' }}>
                                <AlertTriangle size={14} className="me-2" />
                                {suggestionsError}
                            </Alert>
                        )}
                        {!suggestionsLoading && !suggestionsError && suggestions.length === 0 && (
                            <p style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                                {rejectedCount > 0
                                    ? 'Du har avvisat förslagen för den här raden.'
                                    : 'Inga matchningsförslag hittades. Skapa ett verifikat manuellt med knappen längst ner.'}
                            </p>
                        )}
                        {!suggestionsLoading && rejectedCount > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0"
                                    style={{ fontSize: '0.8125rem' }}
                                    onClick={onRestoreRejections}
                                    disabled={actionBusy}
                                >
                                    <Undo2 size={13} className="me-1" />
                                    Visa avvisade förslag igen
                                </Button>
                            </div>
                        )}
                        {/* ONE primary suggestion (the ladder's top pick);
                            alternatives fold behind "Visa fler förslag".
                            When a booked verifikat already matches this line,
                            history alternatives are context only — booking
                            again duplicates (walkthrough finding 2026-07-20). */}
                        {!suggestionsLoading && invoiceChoice.length > 1 && (
                            <Eyebrow>Vilken faktura betalades?</Eyebrow>
                        )}
                        {!suggestionsLoading && cards.filter((c) => !c.llm).flatMap((card) => [selectableCard(card,
                            INVOICE_STRATEGIES.includes(strategyOf(card.suggestion)) ? (
                                invoiceChoice.length > 1 && invoiceChoice.includes(card.suggestion) ? (
                                    <InvoiceMatchCard suggestion={card.suggestion} grouped hideWarnings />
                                ) : (
                                    <InvoiceMatchCard
                                        suggestion={card.suggestion}
                                        grouped={selectable.length > 1}
                                        eyebrow={strategyOf(card.suggestion) === 'supplier_invoice' ? 'Leverantörsbetalning' : 'Fakturabetalning'}
                                    />
                                )
                            ) : (card.suggestion.matchStrategy || card.suggestion.strategy) === 'history' ? (
                                <HistorySuggestionCard
                                    suggestion={card.suggestion}
                                    bankLeg={bankLeg}
                                    busy={actionBusy}
                                    onOpenNeighbor={onOpenNeighbor}
                                    guidanceOnly={hasVerificationMatch}
                                />
                            ) : (
                                <InlineSuggestionCard
                                    suggestion={card.suggestion}
                                    bankLeg={bankLeg}
                                    companyId={companyId}
                                    guidanceOnly={hasVerificationMatch}
                                />
                            ),
                        ),
                        // What applies to the whole choice is said once, under it.
                        invoiceChoice.length > 1 && card.suggestion === invoiceChoice[invoiceChoice.length - 1] ? (
                            <div key="invoice-choice-warnings" style={{ margin: '-4px 0 14px' }}>
                                <SuggestionWarnings warnings={[...new Set(invoiceChoice.flatMap((s) => s.warnings || []))]} />
                            </div>
                        ) : null])}
                        {/* What the footer's primary button will do, once, in small print. */}
                        {!suggestionsLoading && selected?.action.kind === 'confirmPayment' && (
                            <div style={{ fontSize: '0.6875rem', color: '#9ca3af', margin: '-6px 0 16px' }}>
                                Betalningen bokförs på {(bankLeg && bankLeg.account_number) || 'företagskontot'} med bankhändelsens datum.
                            </div>
                        )}
                        {!suggestionsLoading && (hiddenCount > 0 || showAllSuggestions) && suggestions.length > 1 && (
                            <Button
                                variant="link"
                                size="sm"
                                className="text-muted p-0 mb-2"
                                onClick={() => setShowAllSuggestions((v) => !v)}
                            >
                                {showAllSuggestions ? (
                                    <><ChevronUp size={14} className="me-1" />Dölj övriga förslag</>
                                ) : (
                                    <><ChevronDown size={14} className="me-1" />Visa {hiddenCount} {hiddenCount === 1 ? 'förslag till' : 'fler förslag'}</>
                                )}
                            </Button>
                        )}

                        {/* Global-priors evidence (§4.6b): cross-tenant advisory,
                            shown only when the own-history/rule ladder produced no
                            strong suggestion. Informational — never a confirm. */}
                        {!suggestionsLoading && !suggestionsError && prior
                            && !hasStrongSuggestion(suggestions) && (
                            <PriorsEvidenceLine prior={prior} />
                        )}

                        {/* AI-fallback (§4.1 L4): only offered when the ladder came
                            up empty or weak; hidden entirely when the endpoint does
                            not exist (404), disabled with a tooltip when the tenant
                            lacks AI credits (402/403). */}
                        {showLlmCard && selectableCard(cards[cards.length - 1], <LlmSuggestionCard suggestion={llm.suggestion} />)}

                    </>
                )}

                {/* ── Att bekräfta: verdict + one action ────────────────── */}
                {!booking && tier === TIER_MATCH && (
                    <>
                        {/* Same lead words as the "Redan bokförd?"-suggestion
                            cards in the pile — the only difference is
                            certainty: here the engine already made the link
                            (self-originated evidence), there it proposes one. */}
                        <Eyebrow color="#b45309">Redan bokförd</Eyebrow>
                        <p style={{ fontSize: '0.875rem', color: '#1f2937', marginBottom: 18 }}>
                            {line.verification_id
                                ? 'Bokföringen finns redan — motorn har kopplat raden till verifikatet nedan. Bekräfta så är raden klar. Inget nytt bokförs.'
                                : 'Motorn har matchat raden — bekräfta så markeras den som avstämd.'}
                        </p>
                        {linkedVerification && (
                            <div style={{ marginBottom: 20 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem', color: '#374151' }}>
                                    <FileText size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                                    <strong>{linkedVerification.series}{linkedVerification.number}</strong>
                                    <span style={{ color: '#9ca3af' }}>·</span>
                                    <span>{formatDate(linkedVerification.date)}</span>
                                    {linkedVerification.description && (
                                        <>
                                            <span style={{ color: '#9ca3af' }}>·</span>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {linkedVerification.description}
                                            </span>
                                        </>
                                    )}
                                </div>
                                <KonteringSlip rows={(linkedVerification.transactions || []).map((t) => ({
                                    account_number: t.account_number,
                                    account_name: t.account_name,
                                    debit: t.transaction_type === 'debit' ? t.amount : 0,
                                    credit: t.transaction_type === 'credit' ? t.amount : 0,
                                }))} />
                            </div>
                        )}
                    </>
                )}

                {/* ── Klara: verdict + read-only actions ────────────────── */}
                {!booking && tier === TIER_DONE && (
                    <>
                        <Eyebrow color="#10b981">Klar</Eyebrow>
                        <p style={{ fontSize: '0.875rem', color: '#1f2937', marginBottom: 18 }}>
                            {line.verification_id
                                ? 'Raden är bokförd och kopplad till ett verifikat.'
                                : 'Raden är avstämd — inget verifikat behövdes.'}
                        </p>
                        <div className="d-flex flex-wrap gap-2">
                            {line.verification_id && (
                                <Button variant="outline-primary" size="sm" disabled={actionBusy} onClick={onShowVerification}>
                                    <FileText size={14} className="me-1" />
                                    Visa verifikat
                                </Button>
                            )}
                        </div>
                    </>
                )}

                {/* What Mats is doing with the line, and what was already done
                    to it. Both render nothing when there is nothing to say. */}
                {!booking && reconciliation?.agent}
                {!booking && reconciliation?.record}
            </div>

            {/* Fixed bottom bar. Left: the decision (primary + Avvisa) and one
                ⋮ menu with the escape hatches, so nothing to decide hides
                below the fold. Right: position | prev/next | close. */}
            <div className="ll-sheet-footer">
                <div className="ll-sheet-controls">
                    {booking && (
                        <>
                            <button type="button" className="ll-sheet-btn ll-sheet-btn--primary" disabled={bookDisabled} onClick={onBook}>
                                {bookBusy ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14, borderWidth: 2 }} /> : <CheckCircle size={14} />}
                                {bookingPreview ? 'Bekräfta bokföring' : booking.payment ? 'Bokför betalningen' : 'Bokför verifikat'}
                            </button>
                            <button type="button" className="ll-sheet-btn" disabled={bookBusy} onClick={onBookingBackClick}>
                                <ChevronLeft size={14} />
                                Tillbaka
                            </button>
                        </>
                    )}
                    {!booking && tier === TIER_REVIEW && selected && (
                        <>
                            <button type="button" className="ll-sheet-btn ll-sheet-btn--primary" disabled={actionBusy || !!selected.action.disabled} onClick={runPrimary}>
                                {actionBusy ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14, borderWidth: 2 }} /> : <selected.action.icon size={14} />}
                                {reconciliation?.linking ? (selected.action.kind === 'confirmPayment' ? 'Registrerar…' : 'Kopplar…') : selected.action.label}
                            </button>
                            {!selected.llm && (
                                <button type="button" className="ll-sheet-btn" disabled={actionBusy} onClick={() => onRejectSuggestion(selected.suggestion)}>
                                    <XIcon size={14} />
                                    Avvisa
                                </button>
                            )}
                            <span className="ll-sheet-controls__sep" aria-hidden="true" />
                        </>
                    )}
                    {!booking && tier === TIER_MATCH && (
                        <>
                            <button type="button" className="ll-sheet-btn ll-sheet-btn--primary" disabled={actionBusy} onClick={onConfirmSingle}>
                                {actionBusy ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14, borderWidth: 2 }} /> : <CheckCircle size={14} />}
                                {reconciliation?.linking ? 'Kopplar…' : 'Bekräfta'}
                            </button>
                            <span className="ll-sheet-controls__sep" aria-hidden="true" />
                        </>
                    )}
                    {!booking && menuItems.length > 0 && (
                        <Dropdown drop="up" align="start">
                            <Dropdown.Toggle as={KebabToggle} disabled={actionBusy} />
                            <Dropdown.Menu>
                                {menuItems.map((item) => (item.key.startsWith('divider')
                                    ? <Dropdown.Divider key={item.key} />
                                    : (
                                        <Dropdown.Item key={item.key} disabled={item.disabled} onClick={item.onClick}>
                                            {item.icon && <item.icon size={14} />}{item.label}
                                        </Dropdown.Item>
                                    )))}
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                </div>
                <div className="ll-sheet-controls">
                    {position && (
                        <>
                            <span className="ll-sheet-controls__count">
                                {position.index} av {position.total}
                            </span>
                            <span className="ll-sheet-controls__sep" aria-hidden="true" />
                        </>
                    )}
                    <div className="ll-sheet-nav" role="group" aria-label="Bläddra mellan transaktioner">
                        <button type="button" onClick={onPrev} disabled={!onPrev || !!booking} aria-label="Föregående transaktion" title="Föregående (↑)">
                            <ChevronUp size={16} />
                        </button>
                        <button type="button" onClick={onNext} disabled={!onNext || !!booking} aria-label="Nästa transaktion" title="Nästa (↓)">
                            <ChevronDown size={16} />
                        </button>
                    </div>
                    <span className="ll-sheet-controls__sep" aria-hidden="true" />
                    <button type="button" className="ll-sheet-close" onClick={onClose} aria-label="Stäng" title="Stäng (Esc)">
                        <XIcon size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
}

// How a row arrived (bank_statement_lines.source), in the user's words.
const IMPORT_SOURCE_LABELS = {
    psd2_feed: 'Hämtad via bankkoppling',
    csv: 'Importerad från CSV-fil',
    camt: 'Importerad från bankfil',
};

const normalizeBankName = (name) => String(name || '').toLowerCase().replace(/[^a-z0-9åäö]/g, '');

// Initial state for the per-line AI-fallback (§4.1 L4).
const LLM_IDLE = { loading: false, suggestion: null, denied: false, unavailable: false };

export default function BankhandelserPage() {
    const { companyId } = useParams();
    const routerHistory = useHistory();
    const { features } = useFeatures();
    // AI progressive enhancement (B9): the page itself is gated on
    // banking_integration only; ai_reconciliation lights up the AI-heavy
    // affordances and the Automatisering settings link. Byråöversikt is
    // hidden from the UI (route remains behind byra_tier — plan §4.7).
    const hasAiReconciliation = Boolean(features?.ai_reconciliation);
    // PSD2 feed (bank_feed): when the tenant has a live bank connection the
    // header shows sync freshness/consent health instead of pretending CSV
    // import is the only way in. Connection MANAGEMENT lives in Settings →
    // Bankanslutningar; this is status-only.
    const hasBankFeed = Boolean(features?.bank_feed);
    // The contextual assistant entry point rides the same entitlement as the assistant page it
    // navigates to — offering a link to a route the tenant cannot open would be a dead end.
    const hasAiAssistant = Boolean(features?.ai_assistant);
    const [feedConnections, setFeedConnections] = useState([]);

    useEffect(() => {
        if (!hasBankFeed || !companyId) return;
        let cancelled = false;
        import('../../../services/BankConnectionService').then(({ fetchConnections }) =>
            fetchConnections(companyId)
                .then((conns) => {
                    if (!cancelled) setFeedConnections(conns);
                })
                .catch(() => {})
        );
        return () => {
            cancelled = true;
        };
    }, [hasBankFeed, companyId]);

    const [statements, setStatements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Filter/search/sort in the URL. FILTER_ATTENTION is the default, so the
    // clean /bankstatements URL still opens on "what needs me" without
    // carrying ?filter=attention around.
    const {
        search: searchText,
        setSearch: setSearchText,
        debouncedSearch,
        filter: activeFilter,
        setFilter: setActiveFilter,
        sort: sortKey,
        setSort: setSortKey,
    } = useListControls({ defaultFilter: FILTER_ATTENTION, defaultSort: 'date_desc' });
    const [selectedId, setSelectedId] = useState(() => new URLSearchParams(window.location.search).get('line'));
    const [historyLineId] = useState(() => new URLSearchParams(window.location.search).get('line'));
    const [historyLine,setHistoryLine] = useState(null);
    const [rejectedKeys, setRejectedKeys] = useState(() => new Set());
    const [rejectDialog, setRejectDialog] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    // Server-side needs-attention counts (B5) — the summary strip.
    const [attentionCounts, setAttentionCounts] = useState(null);

    const [showImportModal, setShowImportModal] = useState(false);
    // The sheet's inline booking view: null, or { token, initialRows?, manual? }.
    // A new token re-initialises the draft; "Tillbaka" sets it back to null.
    const [booking, setBooking] = useState(null);

    // Batch actions (owner iterations 2026-07-26/27): minimum interaction,
    // nothing hidden. 'Koppla alla' lives under the Att koppla pill (the set
    // IS the filtered list); previous-years dismissal is a quiet one-liner
    // with undo in the toast.
    const [linkAllBusy, setLinkAllBusy] = useState(false);
    // Per-row batch feedback (owner 2026-07-27): the row being processed
    // shows the same dim+spinner as row-loading; finished rows fade out.
    const [linkProgress, setLinkProgress] = useState(null); // { done, total }
    const [processingId, setProcessingId] = useState(null);
    const [leavingIds, setLeavingIds] = useState(() => new Set());
    const [dismissOldBusy, setDismissOldBusy] = useState(false);
    const [actionBusy, setActionBusy] = useState(false);


    // bank account number → mapped BAS account (bank_account_mappings).
    // Drives the account-context line, the detail meta, and — critically —
    // the payment leg of every booking started from this page (owner
    // feedback 2026-07-26: the mapped konto must be used; the tenant's
    // primary_bank chart role is the only fallback — never a hardcoded
    // number, and an unresolved role is an actionable state).
    const [accountMappings, setAccountMappings] = useState({});
    const [roleLegs, setRoleLegs] = useState(null);
    useEffect(() => {
        if (!companyId) return undefined;
        let active = true;
        resolveRoleAccounts(companyId, ['primary_bank', 'tax_account']).then((legs) => {
            if (active) setRoleLegs(legs);
        });
        return () => { active = false; };
    }, [companyId]);
    const roleBankLeg = roleLegs?.primary_bank
        ? { account_number: roleLegs.primary_bank.accountNumber, account_name: roleLegs.primary_bank.accountName }
        : null;
    const roleTaxLeg = roleLegs?.tax_account
        ? { account_number: roleLegs.tax_account.accountNumber, account_name: roleLegs.tax_account.accountName }
        : null;
    const bankLegFor = useCallback(
        (bankAccountNumber) => accountMappings[bankAccountNumber] || null,
        [accountMappings],
    );

    // Inline suggestions for the selected Att granska line (§4.1 surface).
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionGroupId, setSuggestionGroupId] = useState(null);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [suggestionsError, setSuggestionsError] = useState(null);
    const suggestionFetchToken = useRef(0);

    // AI-fallback state for the selected line. `unavailable` (404) hides
    // the button for the session — the endpoint is being rolled out in
    // parallel and may simply not exist yet.
    const [llmState, setLlmState] = useState(LLM_IDLE);
    const llmUnavailableRef = useRef(false);

    // Community-prior evidence for the selected line (§4.6b) — set from
    // the unified-suggestions response (B4), no separate fetch.
    const [prior, setPrior] = useState(null);
    // Inline verifikat for engine-linked to-confirm rows (owner 2026-07-27:
    // the pane must SHOW the matched verifikat, not offer a click).
    const [linkedVerification, setLinkedVerification] = useState(null);
    // Rejection-suppression escape hatch: >0 means förslag are hidden by
    // earlier Avvisa-clicks and can be brought back.
    const [rejectedCount, setRejectedCount] = useState(0);

    // "Visa verifikat" — full verification row for VerificationDetailsModal.
    const [showVerificationDetails, setShowVerificationDetails] = useState(false);
    const [verificationRow, setVerificationRow] = useState(null);

    // Server-reported total backlog size, when the backend sends one —
    // used only for the "Visar X av Y" notice (defensive: 0/absent means
    // we simply don't show it).
    const [totalCount, setTotalCount] = useState(0);
    const [totalIsServer, setTotalIsServer] = useState(false);

    const load = useCallback(async () => {
        if (!companyId) return;
        try {
            const data = await listBankStatements(companyId, { limit: QUEUE_FETCH_LIMIT });
            const fetched = data?.statements || [];
            setStatements(fetched);
            // An invoice-history link may point beyond the first bank page.
            if(historyLineId) setHistoryLine(fetched.find(line=>line.id===historyLineId) || await getBankStatement(companyId,historyLineId));
            const { total, isServerTotal } = extractListTotal(data, fetched.length);
            setTotalCount(total);
            setTotalIsServer(isServerTotal);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Kunde inte hämta banktransaktioner');
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
        // Strip + banner data ride along with every list refresh; failures
        // are silent — a missing banner must never block the queue.
        getAttentionCounts(companyId).then(setAttentionCounts);
        getBankAccountMappings(companyId).then(setAccountMappings);
    }, [companyId,historyLineId]);

    useEffect(() => {
        setIsLoading(true);
        load();
    }, [load]);

    const handleRefresh = useCallback(() => {
        setIsRefreshing(true);
        load();
    }, [load]);

    // "Ladda fler" (the Alla ledger view): append the next server page.
    const [loadingMore, setLoadingMore] = useState(false);
    const handleLoadMore = useCallback(async () => {
        setLoadingMore(true);
        try {
            const data = await listBankStatements(companyId, {
                limit: QUEUE_FETCH_LIMIT,
                offset: statements.length,
            });
            const fetched = data?.statements || [];
            setStatements((prev) => {
                const seen = new Set(prev.map((l) => l.id));
                return [...prev, ...fetched.filter((l) => !seen.has(l.id))];
            });
        } catch (err) {
            toast.error('Kunde inte ladda fler transaktioner');
        } finally {
            setLoadingMore(false);
        }
    }, [companyId, statements.length]);

    const { busy: autoMatchBusyBg, error: suggestionRefreshError, retry: retrySuggestionRefresh } = useBankSuggestionRefresh({
        companyId, statements, isLoading, onRefreshed: load,
    });
    // Learning performed in Settings or another tab becomes visible on return.
    useEffect(() => {
        const refresh = () => { if (document.visibilityState !== 'hidden') load(); };
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        return () => {
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', refresh);
        };
    }, [load]);

    const distinctAccounts = useMemo(
        () => [...new Set(statements.map((l) => l.account_number).filter(Boolean))],
        [statements],
    );

    // Current saldo per account = balance on the NEWEST row that carries
    // one (owner 2026-07-27: the page never showed the saldo). Computed
    // over the full loaded set — the date filter must not change a
    // point-in-time fact.
    const accountBalances = useMemo(() => {
        const latest = {};
        for (const line of statements) {
            if (!line.account_number || line.balance == null || line.balance === '') continue;
            const prev = latest[line.account_number];
            const d = String(bankTransactionDate(line));
            if (!prev || d > prev.date) {
                latest[line.account_number] = { date: d, balance: parseFloat(line.balance) };
            }
        }
        return latest;
    }, [statements]);

    // Per-account filter (multi-bank tenants): clicking an account chip
    // scopes the page to that account; klick again = all accounts.
    const [activeAccount, setActiveAccount] = useState(null);

    // Date-range noise filter (owner 2026-07-27): scope EVERYTHING on the
    // page — pills, counts, batch actions — to the chosen window.
    const [dateRange, setDateRange] = useState('all');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const dateBounds = useMemo(() => {
        const iso = (d) => d.toISOString().slice(0, 10);
        const today = new Date();
        switch (dateRange) {
            case 'm1': { const d = new Date(today); d.setMonth(d.getMonth() - 1); return { from: iso(d), to: null }; }
            case 'm3': { const d = new Date(today); d.setMonth(d.getMonth() - 3); return { from: iso(d), to: null }; }
            case 'year': return { from: `${today.getFullYear()}-01-01`, to: null };
            case 'custom': return { from: customFrom || null, to: customTo || null };
            default: return { from: null, to: null };
        }
    }, [dateRange, customFrom, customTo]);
    const dateFilteredStatements = useMemo(() => {
        return statements.filter((l) => {
            if (activeAccount && l.account_number !== activeAccount) return false;
            if (!dateBounds.from && !dateBounds.to) return true;
            const d = String(bankTransactionDate(l)).slice(0, 10);
            if (dateBounds.from && d < dateBounds.from) return false;
            if (dateBounds.to && d > dateBounds.to) return false;
            return true;
        });
    }, [statements, dateBounds, activeAccount]);

    const filterGroups = useMemo(() => {
        const groups = {
            [FILTER_ATTENTION]: [],
            [FILTER_MATCHED]: [],
            [FILTER_ALL]: dateFilteredStatements,
            [FILTER_DONE]: [],
        };
        for (const line of dateFilteredStatements) {
            const state = deriveDisplayState(line);
            // DISJOINT piles (owner 2026-07-27: subset pills read as
            // duplicates): Att koppla = the engine found an existing
            // booking (mechanical work); Att hantera = needs judgment;
            // Klara = done. Every row lives in exactly ONE work pile.
            if (isBatchConfirmable(line) || isLinkSafe(line)) {
                groups[FILTER_MATCHED].push(line);
            } else if (ATTENTION_STATES.includes(state)) {
                groups[FILTER_ATTENTION].push(line);
            } else {
                groups[FILTER_DONE].push(line);
            }
        }
        return groups;
    }, [dateFilteredStatements]);

    // Previous-years rows still open, EXCLUDING those with a found booking
    // (those belong under Att koppla → Koppla alla). Users who never imported
    // old SIE files know these are booked elsewhere — give them the exit.
    const priorYearCutoff = `${new Date().getFullYear()}-01-01`;
    const oldOpenLines = useMemo(
        () => (filterGroups[FILTER_ATTENTION] || []).filter((l) =>
            String(bankTransactionDate(l)) < priorYearCutoff
            && !isBatchConfirmable(l) && !isLinkSafe(l)),
        [filterGroups, priorYearCutoff],
    );



    const visibleLines = useMemo(() => {
        const lines = filterGroups[activeFilter] || [];
        const needle = debouncedSearch.toLowerCase();
        const filtered = !needle ? lines : lines.filter((l) =>
            (bankTransactionLabel(l) || '').toLowerCase().includes(needle)
            || (bankPaymentMessage(l) || '').toLowerCase().includes(needle)
            || (l.counterparty_name || '').toLowerCase().includes(needle)
            || (l.transaction_type || '').toLowerCase().includes(needle)
            || (l.description || '').toLowerCase().includes(needle)
            || (l.reference || '').toLowerCase().includes(needle)
            || String(l.amount || '').includes(needle));

        const sorted = [...filtered];
        const dateOf = (l) => String(bankTransactionDate(l));
        switch (sortKey) {
            case 'date_asc': sorted.sort((a, b) => dateOf(a).localeCompare(dateOf(b))); break;
            case 'amount_desc': sorted.sort((a, b) => Math.abs(parseFloat(b.amount)) - Math.abs(parseFloat(a.amount))); break;
            case 'amount_asc': sorted.sort((a, b) => Math.abs(parseFloat(a.amount)) - Math.abs(parseFloat(b.amount))); break;
            case 'state': sorted.sort((a, b) =>
                (STATE_ORDER[deriveDisplayState(a)] ?? 9) - (STATE_ORDER[deriveDisplayState(b)] ?? 9)
                || dateOf(b).localeCompare(dateOf(a))); break;
            default: sorted.sort((a, b) => dateOf(b).localeCompare(dateOf(a)));
        }
        return sorted;
    }, [filterGroups, activeFilter, debouncedSearch, sortKey]);

    // Windowed rendering (owner feedback 2026-07-26 — the list was a wall):
    // 50 rows at a time, "Visa fler" extends. Resets whenever the view
    // changes so a filter switch always starts at the top slice.
    const PAGE_SIZE = 50;
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeFilter, debouncedSearch, sortKey, dateRange, customFrom, customTo, activeAccount]);
    const windowedLines = useMemo(
        () => visibleLines.slice(0, visibleCount),
        [visibleLines, visibleCount],
    );

    const selectedLine = useMemo(
        () => visibleLines.find((l) => l.id === selectedId) || (historyLine?.id===selectedId?historyLine:null),
        [visibleLines, selectedId,historyLine],
    );
    const selectedPaneMode = selectedLine ? paneModeFor(deriveDisplayState(selectedLine)) : null;

    // TASK CONTEXT (docs/TASK_CONTEXT_REGISTRY.md §5). Attention is the reconciliation queue (S3);
    // the other filters retain the ordinary bank-transaction detail surface (S1). Derived from
    // `selectedLine` — the selection state this page already owns — so it REPLACES itself when the
    // user picks another row and CLEARS when the page unmounts. It is a hint about what the user
    // is looking at, never a capability: the gateway validates it against the closed registry and
    // the backend authorizes the read independently of it.
    const taskDescriptor = useMemo(
        () => selectedLine
            ? (activeFilter === FILTER_ATTENTION
                ? reconciliationQueueItemDescriptor(selectedLine)
                : bankTransactionDescriptor(selectedLine))
            : null,
        [selectedLine, activeFilter],
    );
    useSurfaceTaskDescriptor(taskDescriptor);

    /**
     * Hand this transaction to the assistant.
     *
     * Through ROUTER STATE rather than the live store, because navigating away unmounts this page
     * and clears the store before the assistant route mounts — the clear is the point, and the
     * hand-off has to survive it. The assistant re-validates what it receives.
     */
    const handleAskAssistant = useCallback((line) => {
        const descriptor = activeFilter === FILTER_ATTENTION
            ? reconciliationQueueItemDescriptor(line)
            : bankTransactionDescriptor(line);
        if (!descriptor) return;
        routerHistory.push(`/${companyId}/ai-assistant`, handOffState(descriptor));
    }, [activeFilter, companyId, routerHistory]);

    // Deferred open (owner feedback 2026-07-26): the panel waits for its
    // data. Clicking a review-mode row sets pendingId — the ROW shows a
    // spinner while unified-suggestions loads, and the panel only opens
    // (setSelectedId) once the answer is in (or errored, so the pane can
    // say so). Non-review rows have nothing to fetch and open instantly.
    // Stale-response guard (audit A4): the token invalidates in-flight
    // responses the moment another row is clicked.
    const [pendingId, setPendingId] = useState(null);

    const handleSelectRow = useCallback((line) => {
        if (line.id === selectedId || line.id === pendingId) return;
        const mode = paneModeFor(deriveDisplayState(line));
        if (mode !== TIER_REVIEW) {
            suggestionFetchToken.current += 1; // cancel any pending fetch
            setPendingId(null);
            setSelectedId(line.id);
            return;
        }
        setPendingId(line.id);
        // Panel already open → swap it to the new row NOW (header renders
        // from list data, body shows loading) instead of lingering on the
        // old row's answer while the new one fetches (owner feedback
        // 2026-07-26). Panel closed → stays closed until data is ready.
        if (selectedId) setSelectedId(line.id);
    }, [selectedId, pendingId]);

    useEffect(() => {
        if (!pendingId) return;
        const token = ++suggestionFetchToken.current;
        // Fresh pane state for the incoming row. A 404 on the AI endpoint
        // sticks for the whole session via llmUnavailableRef.
        setSuggestions([]);
        setSuggestionGroupId(null);
        setSuggestionsError(null);
        setSuggestionsLoading(false);
        setPrior(null);
        setRejectedCount(0);
        setLinkedVerification(null);
        setLlmState({ ...LLM_IDLE, unavailable: llmUnavailableRef.current });
        setSuggestionsLoading(true);

        // Engine-linked rows carry verification_id — fetch the verifikat so
        // the pane can show it inline (rows + konton) before Bekräfta.
        const pendingLine = statements.find((l) => l.id === pendingId);
        if (pendingLine?.verification_id) {
            getStatementVerification(companyId, pendingId)
                .then((data) => {
                    if (suggestionFetchToken.current !== token) return;
                    setLinkedVerification(data?.verification || null);
                })
                .catch(() => {});
        }

        getUnifiedSuggestions(companyId, pendingId)
            .then((data) => {
                if (suggestionFetchToken.current !== token) return;
                setSuggestions(data?.suggestions || []);
                setSuggestionGroupId(data?.suggestion_group_id || null);
                setPrior(data?.prior || null);
                setRejectedCount(data?.rejected_count || 0);
            })
            .catch(() => {
                if (suggestionFetchToken.current !== token) return;
                setSuggestionsError('Kunde inte hämta matchningsförslag');
            })
            .finally(() => {
                if (suggestionFetchToken.current !== token) return;
                setSuggestionsLoading(false);
                setPendingId(null);
                setSelectedId(pendingId);
            });
    }, [companyId, pendingId, statements]);

    // Esc / click-outside / ✕ while booking inline navigates back to the
    // review view first; a second one closes the sheet.
    const closeDetail = useCallback(() => {
        if (booking) setBooking(null);
        else setSelectedId(null);
    }, [booking]);
    // A draft belongs to one line.
    useEffect(() => { setBooking(null); }, [selectedId]);

    // Bank logos for the sheet header — the feed provider's bank list (the
    // same one Inställningar → Bankanslutningar shows; backend-cached). A
    // row names its bank itself (feed: the connected bank; file import: when
    // stamped), else the kontoplan mapping for its account does. No list /
    // no name match → BankLogo falls back to the Landmark glyph.
    const [feedBanks, setFeedBanks] = useState([]);
    useEffect(() => {
        let cancelled = false;
        fetchBanks(companyId)
            .then((data) => { if (!cancelled) setFeedBanks(data?.banks || []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [companyId]);
    const bankFor = useCallback((line) => {
        const mapped = accountMappings[line.account_number];
        const name = line.bank_name || (mapped && mapped.bank_name) || null;
        if (!name) return null;
        const key = normalizeBankName(name);
        const hit = feedBanks.find((b) => normalizeBankName(b.name) === key)
            || feedBanks.find((b) => {
                const n = normalizeBankName(b.name);
                return n && (n.startsWith(key) || key.startsWith(n));
            });
        return { name, logoUrl: (hit && hit.logoUrl) || null };
    }, [accountMappings, feedBanks]);

    // Step the open sheet to the neighbouring row (header chevrons + ↑/↓).
    const selectedIndex = selectedLine ? windowedLines.findIndex((l) => l.id === selectedLine.id) : -1;
    const stepSelection = useCallback((delta) => {
        const next = selectedIndex >= 0 ? windowedLines[selectedIndex + delta] : null;
        if (!next) return;
        handleSelectRow(next);
        const rowEl = document.querySelector(`[data-bank-line="${next.id}"]`);
        if (rowEl && rowEl.scrollIntoView) rowEl.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex, windowedLines, handleSelectRow]);

    useEffect(() => {
        // No row switching under an open draft.
        if (!selectedLine || booking) return undefined;
        const onKeyDown = (e) => {
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
            if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
            // Typing, or a dialog on top, owns the arrow keys.
            if (document.body.classList.contains('modal-open')) return;
            const el = e.target;
            if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
            e.preventDefault();
            stepSelection(e.key === 'ArrowDown' ? 1 : -1);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [selectedLine, booking, stepSelection]);

    // After any mutation (confirm / verifikat / reset / batch) — refetch so
    // tiers reshuffle. The handled line leaves the collection, so the sheet
    // moves on to the next line of the SAME collection (the one below; the
    // one above when it was the last) and the user keeps working without
    // reopening anything. The sheet closes only when nothing is left, or
    // when no line was open (batch actions).
    const handleMutated = useCallback(() => {
        const index = selectedId ? windowedLines.findIndex((l) => l.id === selectedId) : -1;
        const next = index >= 0 ? (windowedLines[index + 1] || windowedLines[index - 1] || null) : null;
        setBooking(null);
        if (next) {
            handleSelectRow(next);
            const rowEl = document.querySelector(`[data-bank-line="${next.id}"]`);
            if (rowEl && rowEl.scrollIntoView) rowEl.scrollIntoView({ block: 'nearest' });
        } else {
            setSelectedId(null);
        }
        setIsRefreshing(true);
        load();
    }, [load, selectedId, windowedLines, handleSelectRow]);

    // "Skapa regel?"-CTA (§4.5 rule synthesis): after a confirm/booking
    // succeeds, fire-and-forget the proposals endpoint; when one matches
    // the just-processed line's counterparty, offer it as a toast with
    // [Skapa regel] (→ Regler prefilled via router state) and [Avvisa]
    // (persisted dismissal; a 503 means NOT dismissed — surfaced).
    const offerRuleProposal = useCallback((line) => {
        if (!line) return;
        getRuleProposals(companyId)
            .then((data) => {
                const proposal = findMatchingProposal(data?.proposals, line);
                if (!proposal) return;
                toast(
                    `Du har bokfört ${proposal.counterparty} likadant ${proposal.occurrences} gånger — skapa en regel?`,
                    {
                        duration: 15000,
                        action: {
                            label: 'Skapa regel',
                            onClick: () => routerHistory.push({
                                pathname: `/${companyId}/settings/automation`,
                                state: { prefillRule: proposalToFormRule(proposal) },
                            }),
                        },
                        cancel: {
                            label: 'Avvisa',
                            onClick: () => {
                                dismissRuleProposal(companyId, proposal.counterparty)
                                    .then(() => toast.success('Regelförslaget avvisades.'))
                                    .catch((err) => toast.error(
                                        err?.response?.status === 503
                                            ? 'Avvisandet kunde inte sparas — förslaget kan dyka upp igen.'
                                            : (err?.response?.data?.message || 'Kunde inte avvisa regelförslaget'),
                                    ));
                            },
                        },
                    },
                );
            })
            .catch(() => {}); // best-effort — the CTA must never block the flow
    }, [companyId, routerHistory]);

    // Group-resolve offer: after ONE booking, offer to book the row's
    // unmatched same-counterparty siblings likadant (server scales the
    // kontering to each row's amount). Best-effort — never blocks.
    const offerBookSimilar = useCallback((line) => {
        if (!line) return;
        getSimilarCount(companyId, line.id).then((count) => {
            if (!count) return;
            toast(
                `${count} liknande ${count === 1 ? 'rad väntar' : 'rader väntar'} — bokför likadant?`,
                {
                    duration: 15000,
                    action: {
                        label: `Bokför ${count} liknande`,
                        onClick: () => {
                            bookSimilar(companyId, line.id)
                                .then((result) => {
                                    const booked = result?.booked || 0;
                                    if (booked > 0) toast.success(`${booked} liknande ${booked === 1 ? 'rad' : 'rader'} bokfördes.`);
                                    if (result?.skipped?.length > 0) {
                                        toast.info(`${result.skipped.length} kunde inte bokföras och ligger kvar i Att hantera.`);
                                    }
                                    handleMutated();
                                })
                                .catch((err) => toast.error(
                                    err?.response?.data?.message || 'Kunde inte bokföra de liknande raderna',
                                ));
                        },
                    },
                },
            );
        });
    }, [companyId, handleMutated]);

    const handleVerificationSuccess = useCallback((result, booked) => {
        // The payment of an invoice is announced as that, with the way to the invoice.
        if (booked?.payment) {
            const { side, invoiceId } = booked.payment;
            toast.success('Betalningen är registrerad och bokförd.', {
                action: { label: 'Visa faktura', onClick: () => routerHistory.push(`/${companyId}/${side === 'supplier' ? 'supplier-invoices' : 'view-invoice'}/${invoiceId}`) },
            });
            handleMutated();
            return;
        }
        toast.success('Verifikatet skapades.');
        const line = selectedLine;
        handleMutated();
        offerBookSimilar(line);
        offerRuleProposal(line);
    }, [handleMutated, selectedLine, offerBookSimilar, offerRuleProposal, companyId, routerHistory]);

    // "Koppla och registrera betalning" on an invoice that is booked when it
    // is paid (kontantmetoden): the kontering step, in the sheet. It opens
    // with a complete kontering from how the company booked this supplier
    // before (word matching and semantic search over earlier verifikat), the
    // same kind of suggestion a row without an invoice gets. With nothing to
    // go on, it opens on the bokföringsmallar.
    const handleBookPayment = useCallback(async (payment) => {
        if (!selectedLine) return;
        const bankLeg = bankLegFor(selectedLine.account_number) || roleBankLeg;
        setActionBusy(true);
        let proposals = [];
        try {
            proposals = (await getPaymentKontering(companyId, selectedLine.id, payment.invoiceId))
                .filter((proposal) => buildHistoryPrefillRows(proposal, bankLeg));
        } catch (err) {
            // The step works without a proposal; the user is not stopped by a failed search.
            proposals = [];
        } finally {
            setActionBusy(false);
        }
        setBooking(proposals.length > 0
            ? { token: Date.now(), payment, proposals, initialRows: buildHistoryPrefillRows(proposals[0], bankLeg) }
            : { token: Date.now(), payment, proposals, manual: true });
    }, [companyId, selectedLine, bankLegFor, roleBankLeg]);

    // Import "Klar" — the import response carries the auto-matching outcome
    // (B3): tell the user what the engine already did for them.
    const handleImportSuccess = useCallback((result) => {
        setShowImportModal(false);
        const s = result?.matching?.summary;
        if (s && (s.autoMatched > 0 || s.suggested > 0)) {
            toast.success(
                `${s.autoMatched} transaktioner matchades automatiskt, ${s.suggested} fick förslag — se Att hantera.`,
                { duration: 8000 },
            );
        } else if (result?.matching?.mode === 'enqueued') {
            toast.info('Transaktionerna matchas i bakgrunden — uppdatera listan om en stund.');
        }
        handleMutated();
    }, [handleMutated]);

    // Confirm one inline suggestion — the single confirm path for ALL
    // strategy types (POST .../confirm-suggestion).
    const handleConfirmSuggestion = useCallback(async (suggestion, { registerPayment = false } = {}) => {
        if (!selectedLine) return;
        const targets = buildConfirmTargets(suggestion);
        if (targets.length === 0) return;
        setActionBusy(true);
        try {
            await confirmSuggestion(companyId, selectedLine.id, {
                suggestionGroupId,
                strategy: suggestion.matchStrategy || suggestion.strategy,
                targets,
                registerPayment,
            });
            toast.success('Matchningen bekräftades.');
            const line = selectedLine;
            handleMutated();
            offerRuleProposal(line);
        } catch (err) {
            if (err?.response?.status === 409) {
                toast.error('Redan matchad — uppdatera listan.');
                handleMutated();
            } else {
                toast.error(err?.response?.data?.message || err.message || 'Kunde inte bekräfta matchningen');
            }
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, suggestionGroupId, handleMutated, offerRuleProposal]);

    // Transfer suggestions have no linkable target — "confirm" is booking
    // the transfer verifikat (skattekonto ↔ the line's MAPPED bank konto).
    const handleBookTransfer = useCallback((suggestion) => {
        if (!selectedLine) return;
        const amount = round2(Math.abs(parseFloat(selectedLine.amount)));
        const incoming = parseFloat(selectedLine.amount) > 0;
        const leg = bankLegFor(selectedLine.account_number) || roleBankLeg;
        // Both legs come from the tenant's own chart (mapping or role) —
        // an unresolved role is surfaced, never replaced by a pinned number.
        if (!leg || !roleTaxLeg) {
            toast.error(
                !leg
                    ? 'Rollen Företagskonto är inte mappad — mappa den under Inställningar → Bokföring → Kontoplansroller.'
                    : 'Rollen Skattekonto är inte mappad — mappa den under Inställningar → Bokföring → Kontoplansroller.'
            );
            return;
        }
        const bankRow = {
            account_number: leg.account_number,
            account_name: leg.account_name,
            debit: incoming ? amount : '',
            credit: incoming ? '' : amount,
        };
        const counterRow = {
            account_number: roleTaxLeg.account_number,
            account_name: roleTaxLeg.account_name,
            debit: incoming ? '' : amount,
            credit: incoming ? amount : '',
        };
        setBooking({ token: Date.now(), initialRows: [bankRow, counterRow] });
    }, [selectedLine, bankLegFor, roleBankLeg, roleTaxLeg]);

    // History suggestion "Bokför så här" — an L3 candidate proposes a
    // BOOKING (scaled counter rows + our bank leg from the account
    // mapping), so it opens the wizard prefilled instead of hitting the
    // confirm-suggestion endpoint.
    const handlePrefillHistoryBooking = useCallback((suggestion) => {
        setBooking({
            token: Date.now(),
            initialRows: buildHistoryPrefillRows(
                suggestion,
                (selectedLine ? bankLegFor(selectedLine.account_number) : null) || roleBankLeg,
            ),
        });
    }, [selectedLine, bankLegFor, roleBankLeg]);

    // Neighbor chip ("V12 · 2026-03-04") — fetch the precedent verifikat
    // by id (GET /:companyId/verification/:id — the shared accounting
    // endpoint; neighbors are not statement-scoped) and open the modal.
    const handleOpenNeighbor = useCallback(async (neighbor) => {
        if (!neighbor?.id) return;
        setActionBusy(true);
        try {
            const verification = await getVerificationById(companyId, neighbor.id);
            // The endpoint returns the verification directly keyed on
            // verification_id (not id) — accept either.
            const vid = verification && (verification.verification_id || verification.id);
            if (!vid) {
                toast.error('Verifikatet kunde inte hämtas.');
                return;
            }
            let debitSum = 0;
            let creditSum = 0;
            (verification.transactions || []).forEach((t) => {
                const rowAmount = parseFloat(t.amount) || 0;
                if (t.transaction_type === 'debit') debitSum += rowAmount;
                else creditSum += rowAmount;
            });
            setVerificationRow({ ...verification, id: vid, debitSum: round2(debitSum), creditSum: round2(creditSum) });
            setShowVerificationDetails(true);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Kunde inte hämta verifikatet');
        } finally {
            setActionBusy(false);
        }
    }, [companyId]);

    // "Fråga AI" — the entitlement-gated LLM fallback. 402/403 = no AI
    // credits (toast + disabled button w/ tooltip); 404 = endpoint absent
    // (hide for the session); anything else is a real error.
    const handleAskAi = useCallback(async () => {
        if (!selectedLine) return;
        setLlmState((prev) => ({ ...prev, loading: true }));
        try {
            const data = await getLlmSuggestion(companyId, selectedLine.id);
            const suggestion = data?.suggestion || data;
            if (!suggestion || (!Array.isArray(suggestion.rows) && !suggestion.rationale)) {
                setLlmState((prev) => ({ ...prev, loading: false }));
                toast.info('AI:n hittade inget förslag för den här transaktionen.');
                return;
            }
            setLlmState({ loading: false, suggestion, denied: false, unavailable: false });
        } catch (err) {
            const kind = llmDenialKind(err);
            if (kind === 'absent') {
                llmUnavailableRef.current = true;
                setLlmState({ ...LLM_IDLE, unavailable: true });
            } else if (kind === 'no_credits') {
                toast.error('AI-krediter saknas');
                setLlmState({ ...LLM_IDLE, denied: true });
            } else {
                setLlmState((prev) => ({ ...prev, loading: false }));
                toast.error(err?.response?.data?.message || err.message || 'AI-förslaget kunde inte hämtas');
            }
        }
    }, [companyId, selectedLine]);

    // AI-card confirm = prefill the booking wizard (the LLM proposes a
    // booking, not a match) — never the confirm-suggestion endpoint.
    const handleUseLlmSuggestion = useCallback((suggestion) => {
        const rows = normalizeSuggestionRows(suggestion?.rows);
        setBooking(rows.length >= 2 ? { token: Date.now(), initialRows: rows } : { token: Date.now(), manual: true });
    }, []);

    // "Avvisa" — the learning loop's reject event (audit D6). Opens the
    // reason dialog; the actual submit lives in submitReject below.
    const handleRejectSuggestion = useCallback((suggestion) => {
        setRejectReason('');
        setRejectDialog(suggestion);
    }, []);

    const submitReject = useCallback(async () => {
        const suggestion = rejectDialog;
        if (!suggestion || !selectedLine) return;
        setActionBusy(true);
        try {
            await submitMatchingFeedback(companyId, {
                statementId: selectedLine.id,
                suggestionGroupId,
                event: 'rejected',
                reason: rejectReason.trim() || undefined,
                // Server-side suppression (Phase B): name the rejected
                // suggestion so it stays suppressed across sessions — the
                // rejectedKeys set below is now just an optimistic overlay.
                suggestion: {
                    strategy: suggestion.matchStrategy || suggestion.strategy || null,
                    candidate_id: suggestion.candidateId || null,
                    retrieval_source: suggestion.retrievalSource || null,
                    // Kontering signature (2026-07-26): rejecting THIS
                    // proposal must not hide a different one on the line.
                    rows: (suggestion.rows || suggestion.templateRows || []).map((r) => ({
                        account_number: r.account_number,
                        side: r.side || (parseFloat(r.debit) > 0 ? 'debit' : 'credit'),
                    })),
                },
            });
            setRejectedKeys((prev) => {
                const next = new Set(prev);
                next.add(rejectionKey(selectedLine.id, suggestion));
                return next;
            });
            setSuggestions((prev) => prev.filter((s) => s !== suggestion));
            setRejectedCount((c) => c + 1);
            setRejectDialog(null);
            toast.success('Förslaget avvisades.');
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte avvisa förslaget');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, suggestionGroupId, rejectDialog, rejectReason]);

    // Review-tier escape hatch for lines reconciled outside LedgerLoop.
    // Undo rejection suppression ("Visa förslagen igen") — server deletes
    // the line's rejected-events + refreshes its snapshot; we refetch the
    // pane (setPendingId reruns the fetch) and the list badge in place
    // WITHOUT dropping the selection.
    const handleRestoreRejections = useCallback(async () => {
        if (!selectedLine) return;
        setActionBusy(true);
        try {
            const result = await restoreRejectedSuggestions(companyId, selectedLine.id);
            const n = result?.restored || 0;
            toast.success(n > 0 ? 'Förslagen visas igen.' : 'Inga avvisade förslag att återställa.');
            setPendingId(selectedLine.id);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte återställa förslagen');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, load]);

    const handleMarkReconciledExternal = useCallback(async () => {
        if (!selectedLine) return;
        setActionBusy(true);
        try {
            await bulkMarkReconciled(companyId, [selectedLine.id], {
                status: 'reconciled_external',
                notes: 'Markerad som avstämd externt via Matchning',
            });
            toast.success('Transaktionen markerades som avstämd externt.');
            handleMutated();
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte markera som avstämd');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, handleMutated]);

    // Matchade tier: confirm one line as reconciled (status → verified).
    const handleConfirmSingle = useCallback(async () => {
        if (!selectedLine) return;
        setActionBusy(true);
        try {
            await bulkMarkReconciled(companyId, [selectedLine.id], {
                status: 'verified',
                notes: 'Bekräftad via Matchning',
            });
            toast.success('Transaktionen bekräftades som avstämd.');
            handleMutated();
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte bekräfta transaktionen');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, handleMutated]);

    // "Ångra" — reset a matched/skipped/externally-reconciled line back to
    // unmatched (the promised undo, audit D4). 409 = raced another change.
    const handleReset = useCallback(async () => {
        if (!selectedLine) return;
        setActionBusy(true);
        try {
            await resetStatement(companyId, selectedLine.id);
            toast.success('Raden återställdes till omatchad.');
            handleMutated();
        } catch (err) {
            if (err?.response?.status === 409) {
                toast.error('Kunde inte ångra — raden har ändrats. Uppdatera listan.');
                handleMutated();
            } else {
                toast.error(err?.response?.data?.message || err.message || 'Kunde inte ångra');
            }
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, handleMutated]);

    // "Visa verifikat" — fetch the statement's verification and open the
    // shared VerificationDetailsModal (read-only) with computed sums.
    const handleShowVerification = useCallback(async () => {
        if (!selectedLine) return;
        setActionBusy(true);
        try {
            const data = await getStatementVerification(companyId, selectedLine.id);
            const verification = data?.verification;
            if (!verification) {
                toast.error('Inget verifikat hittades för raden.');
                return;
            }
            let debitSum = 0;
            let creditSum = 0;
            (verification.transactions || []).forEach((t) => {
                const rowAmount = parseFloat(t.amount) || 0;
                if (t.transaction_type === 'debit') debitSum += rowAmount;
                else creditSum += rowAmount;
            });
            setVerificationRow({ ...verification, debitSum: round2(debitSum), creditSum: round2(creditSum) });
            setShowVerificationDetails(true);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Kunde inte hämta verifikatet');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine]);


    // "Koppla alla" (Att koppla pill): rows are processed ONE BY ONE so the
    // batch is visible work, not a spinner-and-pray — the active row dims
    // with a spinner, done rows fade out of Att koppla (their status flips
    // locally; a quiet reload at the end restores server truth). Scoped to
    // the rows currently in view (date window applies). Link-only and
    // per-row undoable, so no confirm dialog.
    const handleLinkAll = useCallback(async () => {
        const matchedLines = [...(filterGroups[FILTER_MATCHED] || [])];
        if (matchedLines.length === 0) return;
        setLinkAllBusy(true);
        setLinkProgress({ done: 0, total: matchedLines.length });
        let handled = 0;
        let alreadyDone = 0;
        let skipped = 0;
        let needsPreview = 0;
        try {
            for (const line of matchedLines) {
                setProcessingId(line.id);
                let ok = false;
                let already = false;
                try {
                    // Both kinds go through the same backend call: a row the
                    // engine pre-linked at import (Att bekräfta) and a row
                    // with a certain verifikat match. The backend runs the
                    // link through the reconciliation flow, one row each.
                    const result = await linkSafeMatches(companyId, [line.id]);
                    // A row someone else (another tab, an earlier run)
                    // already handled is DONE, not skipped — it fades
                    // out like a success (2026-07-27 incident).
                    already = (result?.already_handled || []).length > 0;
                    ok = (result?.linked || 0) > 0 || already;
                    // An invoice match registers a payment, which the user
                    // previews row by row — say so instead of "skipped".
                    if (!ok && (result?.skipped || []).some((r) => /fakturabetalning/i.test(r.reason || ''))) needsPreview += 1;
                } catch (rowError) {
                    ok = false;
                }
                if (ok) {
                    if (already) alreadyDone += 1; else handled += 1;
                    // Fade the row out, then flip its local status so it
                    // leaves Matchade (server truth reloaded at the end).
                    setLeavingIds((prev) => new Set(prev).add(line.id));
                    setTimeout(() => {
                        setStatements((prev) => prev.map((l) =>
                            l.id === line.id ? { ...l, reconciliation_status: 'verified' } : l));
                        setLeavingIds((prev) => {
                            const next = new Set(prev);
                            next.delete(line.id);
                            return next;
                        });
                    }, 300);
                } else {
                    skipped += 1;
                }
                setProcessingId(null);
                setLinkProgress((p) => ({ done: (p?.done || 0) + 1, total: p?.total || matchedLines.length }));
            }
            if (handled > 0) toast.success(`${handled} ${handled === 1 ? 'rad' : 'rader'} kopplade — inget nytt bokfördes.`);
            if (alreadyDone > 0) {
                toast.info(`${alreadyDone} ${alreadyDone === 1 ? 'rad var' : 'rader var'} redan klara sedan tidigare.`);
            }
            if (needsPreview > 0) {
                toast.info(
                    `${needsPreview} ${needsPreview === 1 ? 'fakturabetalning ligger' : 'fakturabetalningar ligger'} kvar — öppna raden och bekräfta betalningen.`,
                    { duration: 8000 },
                );
            }
            if (skipped - needsPreview > 0) {
                const rest = skipped - needsPreview;
                toast.info(
                    `${rest} ${rest === 1 ? 'rad hoppades' : 'rader hoppades'} över vid extrakontrollen och ligger kvar.`,
                    { duration: 8000 },
                );
            }
            load();
        } finally {
            setLinkAllBusy(false);
            setLinkProgress(null);
            setProcessingId(null);
        }
    }, [companyId, filterGroups, load]);

    // Previous-years dismissal (owner reasoning 2026-07-27: old rows are
    // often already booked in a previous system whose SIE was never
    // imported — the matcher CANNOT find those). Marks them avstämda
    // (reconciled_external, nothing booked); undo for the whole batch
    // lives in the toast, per-row Ångra under Klara.
    const handleDismissOld = useCallback(async () => {
        const ids = oldOpenLines.map((l) => l.id);
        if (ids.length === 0) return;
        setDismissOldBusy(true);
        try {
            await bulkMarkReconciled(companyId, ids, {
                status: 'reconciled_external',
                notes: 'Markerad som bokförd i tidigare system (rader från tidigare år)',
            });
            toast.success(`${ids.length} rader från tidigare år markerades som redan bokförda.`, {
                duration: 12000,
                action: {
                    label: 'Ångra',
                    onClick: () => {
                        bulkUnmatchStatements(companyId, ids)
                            .then(() => { toast.success('Raderna återställdes.'); handleMutated(); })
                            .catch(() => toast.error('Kunde inte ångra allt — använd Ångra per rad under Klara.'));
                    },
                },
            });
            handleMutated();
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte markera raderna');
        } finally {
            setDismissOldBusy(false);
        }
    }, [companyId, oldOpenLines, handleMutated]);

    // Summary-strip numbers: server-side attention counts when available,
    // else derived from the loaded window.
    // With an active date window the strip must describe what's VISIBLE —
    // the server-side counts are always all-time, so derive locally then.
    const dateWindowActive = Boolean(dateBounds.from || dateBounds.to);
    const stripCounts = (!dateWindowActive && attentionCounts) || {
        to_confirm: filterGroups[FILTER_ATTENTION].filter((l) => deriveDisplayState(l) === DISPLAY_TO_CONFIRM).length,
        has_suggestion: filterGroups[FILTER_ATTENTION].filter((l) => deriveDisplayState(l) === DISPLAY_SUGGESTED).length,
        needs_you: filterGroups[FILTER_ATTENTION].filter((l) => deriveDisplayState(l) === DISPLAY_NEEDS_YOU).length,
    };
    const bookedCount = filterGroups[FILTER_DONE].length;

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <PageHeader
                    icon={Landmark}
                    title="Bankhändelser"
                    subtitle={
                        feedConnections.length > 0
                            ? 'Granska och bokför dina banktransaktioner — hämtas automatiskt från banken.'
                            : 'Importera, granska och bokför dina banktransaktioner.'
                    }
                >
                    {feedConnections.length > 0 && (() => {
                        const newestSync = feedConnections
                            .map((c) => c.last_sync_at)
                            .filter(Boolean)
                            .sort()
                            .pop();
                        const needsRenewal = feedConnections.some(
                            (c) => c.status === 'expiring' || c.status === 'expired'
                        );
                        return (
                            <p style={{ margin: '2px 0 0', fontSize: 12, color: needsRenewal ? '#b45309' : '#9ca3af' }}>
                                {needsRenewal ? (
                                    <Link to={`/${companyId}/settings/bank-connections`} style={{ color: '#b45309', fontWeight: 600 }}>
                                        Bankmedgivande behöver förnyas med BankID →
                                    </Link>
                                ) : (
                                    <>
                                        Senast hämtad från banken:{' '}
                                        {newestSync ? moment(newestSync).format('YYYY-MM-DD HH:mm') : 'väntar på första hämtningen'}
                                    </>
                                )}
                            </p>
                        );
                    })()}
                </PageHeader>

                {/* Summary strip (§4.3): what the system did vs what needs you */}
                {!isLoading && statements.length > 0 && (
                    <div
                        className="matching-state-box"
                        style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16, padding: '12px 16px' }}
                    >
                        <span style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <CheckCircle size={15} style={{ color: '#10b981' }} />
                            <strong>{bookedCount}</strong> bokförda/klara
                        </span>
                        <span style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <CheckCircle size={15} style={{ color: '#b45309' }} />
                            <strong>{stripCounts.to_confirm}</strong> att bekräfta
                        </span>
                        <span style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Sparkles size={15} style={{ color: 'var(--primary)' }} />
                            <strong>{stripCounts.has_suggestion}</strong> med förslag
                        </span>
                        <span style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <AlertCircle size={15} style={{ color: '#dc2626' }} />
                            <strong>{stripCounts.needs_you}</strong> behöver dig
                        </span>
                        {autoMatchBusyBg && (
                            <span style={{ fontSize: 12, color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Spinner animation="border" size="sm" />
                                Uppdaterar förslag…
                            </span>
                        )}
                        {suggestionRefreshError && (
                            <span role="alert" style={{ fontSize: 12, color: '#b45309' }}>
                                {suggestionRefreshError}{' '}
                                <button type="button" className="btn btn-link btn-sm p-0" onClick={retrySuggestionRefresh}>Försök igen</button>
                            </span>
                        )}
                    </div>
                )}

                {/* Bank-account context (owner feedback 2026-07-26): which
                    bankkonto these rows belong to and WHICH kontoplan-konto
                    it books against. Unmapped accounts get the inline
                    "koppla" nudge — bookings fall back to the primary_bank role until then. */}
                {distinctAccounts.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                        {distinctAccounts.map((acct) => {
                            const leg = accountMappings[acct];
                            return leg ? (
                                <span
                                    key={acct}
                                    role={distinctAccounts.length > 1 ? 'button' : undefined}
                                    onClick={distinctAccounts.length > 1
                                        ? () => { setActiveAccount((a) => (a === acct ? null : acct)); setSelectedId(null); }
                                        : undefined}
                                    title={distinctAccounts.length > 1 ? 'Visa endast detta konto' : undefined}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        border: activeAccount === acct ? '1px solid var(--primary)' : '1px solid #e5e7eb',
                                        background: activeAccount === acct ? 'var(--rgba-primary-1)' : '#fff',
                                        borderRadius: 999, padding: '4px 12px',
                                        fontSize: 12, color: '#374151',
                                        fontVariantNumeric: 'tabular-nums',
                                        cursor: distinctAccounts.length > 1 ? 'pointer' : 'default',
                                    }}
                                >
                                    <Landmark size={12} style={{ color: '#6b7280' }} />
                                    {acct}
                                    <span style={{ color: '#9ca3af' }}>bokförs på</span>
                                    <strong>{leg.account_number}</strong>
                                    <span style={{ color: '#6b7280' }}>{leg.account_name}</span>
                                    {accountBalances[acct] && (
                                        <>
                                            <span style={{ color: '#9ca3af' }}>·</span>
                                            <span style={{ color: '#9ca3af' }}>Saldo</span>
                                            <strong>{formatCurrency(accountBalances[acct].balance)}</strong>
                                        </>
                                    )}
                                </span>
                            ) : (
                                <Link
                                    key={acct}
                                    to={`/${companyId}/settings/bank-connections`}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        border: '1px solid #fcd34d', background: '#fffbeb',
                                        borderRadius: 999, padding: '4px 12px',
                                        fontSize: 12, color: '#92400e', textDecoration: 'none',
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                    title="Utan koppling bokförs raderna mot rollen Företagskonto — koppla kontot till din kontoplan"
                                >
                                    <AlertTriangle size={12} />
                                    {acct}
                                    <span>saknar konto i kontoplanen — koppla</span>
                                </Link>
                            );
                        })}
                    </div>
                )}

                <ListToolbar
                    search={{
                        value: searchText,
                        onChange: setSearchText,
                        placeholder: 'Sök beskrivning, referens, belopp…',
                        ariaLabel: 'Sök bland bankhändelser',
                    }}
                    sort={{
                        options: SORT_OPTIONS,
                        value: sortKey,
                        onChange: (v) => setSortKey(v || 'date_desc'),
                        ariaLabel: 'Sortera bankhändelser',
                    }}
                    actions={
                        <>
                            <button type="button" className="ll-btn ll-btn--secondary" onClick={handleRefresh} disabled={isRefreshing}>
                                <RefreshCw className={isRefreshing ? 'spin' : undefined} />
                                Uppdatera
                            </button>
                            <button type="button" className="ll-btn ll-btn--primary" onClick={() => setShowImportModal(true)}>
                                <Upload />
                                Importera
                            </button>
                            {/* Rules/auto-post/AI-historik config lives in
                                Inställningar → Automatisering (Phase D) */}
                            {hasAiReconciliation && (
                                <Link
                                    to={`/${companyId}/settings/automation`}
                                    className="ll-btn ll-btn--secondary"
                                    title="Regler och automatisering"
                                >
                                    <SlidersHorizontal />
                                    Automatisering
                                </Link>
                            )}
                        </>
                    }
                >
                    <div style={{ minWidth: 170 }}>
                        <Select
                            options={DATE_RANGE_OPTIONS}
                            value={DATE_RANGE_OPTIONS.find((o) => o.value === dateRange) || DATE_RANGE_OPTIONS[0]}
                            onChange={(opt) => setDateRange(opt?.value || 'all')}
                            isSearchable={false}
                            theme={brandSelectTheme}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            styles={{
                                control: (base) => ({ ...base, minHeight: 38, fontSize: 13 }),
                                option: (base) => ({ ...base, fontSize: 13 }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            aria-label="Filtrera på datum"
                        />
                    </div>
                    {dateRange === 'custom' && (
                        <>
                            <Form.Control
                                type="date"
                                value={customFrom}
                                onChange={(e) => setCustomFrom(e.target.value)}
                                aria-label="Från datum"
                                style={{ width: 150 }}
                            />
                            <span style={{ color: '#9ca3af', fontSize: 13 }}>–</span>
                            <Form.Control
                                type="date"
                                value={customTo}
                                onChange={(e) => setCustomTo(e.target.value)}
                                aria-label="Till datum"
                                style={{ width: 150 }}
                            />
                        </>
                    )}
                </ListToolbar>

                <FilterChips
                    chips={FILTERS.map((c) => ({ ...c, count: (filterGroups[c.key] || []).length }))}
                    value={activeFilter}
                    onChange={(key) => { setActiveFilter(key); setSelectedId(null); }}
                />

                {/* Att koppla: the one certain batch — these rows already
                    HAVE a booking; linking them is the whole job. One
                    sentence, one button, no dialog (link-only + per-row
                    Ångra = nothing to warn about). */}
                {activeFilter === FILTER_MATCHED && (filterGroups[FILTER_MATCHED] || []).length > 0 && (
                    <div
                        className="matching-state-box"
                        style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 16px' }}
                    >
                        <span style={{ fontSize: 13, color: '#374151' }}>
                            Dessa rader har redan en bokföring — koppla ihop dem så är de klara.
                            Inget nytt bokförs och varje rad kan ångras.
                        </span>
                        {/* The app's own button, as on the invoice pages. */}
                        <button
                            type="button"
                            className="ll-btn ll-btn--primary"
                            style={{ marginLeft: 'auto' }}
                            onClick={handleLinkAll}
                            disabled={linkAllBusy}
                        >
                            {linkAllBusy ? (
                                <>
                                    <Spinner size="sm" />
                                    Kopplar… {linkProgress ? `${linkProgress.done}/${linkProgress.total}` : ''}
                                </>
                            ) : `Koppla alla (${(filterGroups[FILTER_MATCHED] || []).length})`}
                        </button>
                    </div>
                )}

                {/* Previous-years exit (quiet): rows the matcher can never
                    find because their bokföring lives in a system whose SIE
                    was never imported. Batch undo in the toast. */}
                {activeFilter === FILTER_ATTENTION && oldOpenLines.length > 0 && (
                    <div style={{ fontSize: 13, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span>
                            {oldOpenLines.length} rader är från tidigare år — redan bokförda i ett
                            annat system?
                        </span>
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            style={{ fontSize: 13 }}
                            onClick={handleDismissOld}
                            disabled={dismissOldBusy}
                        >
                            {dismissOldBusy ? 'Markerar…' : 'Markera dem som avstämda'}
                        </Button>
                    </div>
                )}

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-0">
                        <AlertCircle size={14} className="me-2" />
                        {error}
                    </Alert>
                )}

                {/* The list keeps its full width; the selected row's detail
                    slides in OVER the page from the right edge (SideSheet,
                    owner decision 2026-09-19 — replaces the 2026-07-26
                    beside-the-list split). The sheet is non-modal: rows stay
                    clickable and ↑/↓ steps through them while it is open. */}
                <>
                    <div>
                        {isLoading ? (
                            <div className="matching-state-box" style={{ padding: 40, textAlign: 'center' }}>
                                <Spinner animation="border" size="sm" />
                                <span className="ms-2">Laddar transaktioner…</span>
                            </div>
                        ) : visibleLines.length === 0 ? (
                            statements.length === 0 ? (
                                /* Cold start — nothing imported yet */
                                <div className="matching-state-box" style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                                    <Landmark size={32} style={{ opacity: 0.4 }} />
                                    <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 500 }}>
                                        Importera dina banktransaktioner
                                    </p>
                                    <p style={{ fontSize: 13, marginBottom: 12 }}>
                                        Ladda upp din banks kontoutdrag (CSV) så matchar och
                                        föreslår LedgerLoop bokföringen automatiskt — och lär
                                        sig av varje rad du bokför.
                                    </p>
                                    <Button variant="primary" size="sm" onClick={() => setShowImportModal(true)}>
                                        <Upload size={14} className="me-1" />
                                        Importera transaktioner
                                    </Button>
                                </div>
                            ) : activeFilter === FILTER_ATTENTION && !searchText ? (
                                /* The goal state — positive, not a blank table */
                                <div className="matching-state-box" style={{ padding: 40, textAlign: 'center' }}>
                                    <CheckCircle size={32} style={{ color: '#10b981' }} />
                                    <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 600, color: '#111827' }}>
                                        {(filterGroups[FILTER_MATCHED] || []).length > 0
                                            ? 'Inget behöver din bedömning'
                                            : 'Allt är hanterat'}
                                    </p>
                                    <p style={{ fontSize: 13, marginBottom: 0, color: '#6b7280' }}>
                                        {bookedCount > 0
                                            ? `${bookedCount} rader är bokförda eller avstämda. Importera fler transaktioner när du vill.`
                                            : 'Importera fler transaktioner när du vill.'}
                                    </p>
                                    {(filterGroups[FILTER_MATCHED] || []).length > 0 && (
                                        <Button
                                            variant="outline-success"
                                            size="sm"
                                            style={{ marginTop: 12 }}
                                            onClick={() => { setActiveFilter(FILTER_MATCHED); setSelectedId(null); }}
                                        >
                                            {(filterGroups[FILTER_MATCHED] || []).length} rader väntar under Att koppla
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <EmptyState
                                    variant="no-matches"
                                    query={debouncedSearch}
                                    title={debouncedSearch ? 'Inga träffar' : 'Inga transaktioner i denna vy'}
                                    description="Byt filter för att se andra transaktioner."
                                />
                            )
                        ) : (
                            <div className="matching-list-box">
                                {windowedLines.map((line) => (
                                    <div
                                        key={line.id}
                                        data-bank-line={line.id}
                                        style={{
                                            transition: 'opacity 300ms ease, transform 300ms ease',
                                            opacity: leavingIds.has(line.id) ? 0 : 1,
                                            transform: leavingIds.has(line.id) ? 'translateX(16px)' : 'none',
                                        }}
                                    >
                                        <StatementRow
                                            line={line}
                                            bankLeg={bankLegFor(line.account_number) || roleBankLeg}
                                            isSelected={line.id === selectedId}
                                            isPending={line.id === pendingId || line.id === processingId}
                                            disablePreview={Boolean(selectedLine || pendingId) || linkAllBusy}
                                            onSelect={() => handleSelectRow(line)}
                                        />
                                    </div>
                                ))}
                                {/* Windowed rendering: 50 at a time; the Alla
                                    view chains into server paging once the
                                    loaded window is exhausted. */}
                                {visibleLines.length > windowedLines.length ? (
                                    <div style={{ padding: 12, textAlign: 'center', borderTop: '1px solid #f3f4f6' }}>
                                        <Button variant="outline-secondary" size="sm" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
                                            Visa fler (visar {windowedLines.length} av {visibleLines.length})
                                        </Button>
                                    </div>
                                ) : activeFilter === FILTER_ALL && totalIsServer && totalCount > statements.length && (
                                    <div style={{ padding: 12, textAlign: 'center', borderTop: '1px solid #f3f4f6' }}>
                                        <Button variant="outline-secondary" size="sm" onClick={handleLoadMore} disabled={loadingMore}>
                                            {loadingMore ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-1" />
                                                    Laddar…
                                                </>
                                            ) : `Ladda fler (${statements.length} av ${totalCount})`}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Light-dismiss: a click anywhere that is not a row (rows swap
                        the sheet's content) or a stacked modal/popover closes it. */}
                    <SideSheet
                        open={Boolean(selectedLine)}
                        onClose={closeDetail}
                        label="Transaktionsdetaljer"
                        closeOnClickOutside
                        keepOpenWithin=".matching-list-item"
                    >
                    {selectedLine && (
                        <ReconciliationWorkspace key={selectedLine.id} companyId={companyId} line={selectedLine} onSettled={handleMutated}>
                        <StatementDetail
                            line={selectedLine}
                            bankLeg={bankLegFor(selectedLine.account_number) || roleBankLeg}
                            companyId={companyId}
                            tier={selectedPaneMode}
                            onClose={closeDetail}
                            onPrev={selectedIndex > 0 ? () => stepSelection(-1) : undefined}
                            onNext={selectedIndex >= 0 && selectedIndex < windowedLines.length - 1 ? () => stepSelection(1) : undefined}
                            position={selectedIndex >= 0 ? { index: selectedIndex + 1, total: visibleLines.length } : null}
                            bank={bankFor(selectedLine)}
                            suggestions={suggestions.filter(
                                (s) => !selectedLine || !rejectedKeys.has(rejectionKey(selectedLine.id, s))
                            )}
                            suggestionsLoading={suggestionsLoading}
                            suggestionsError={suggestionsError}
                            actionBusy={actionBusy}
                            onConfirmSuggestion={handleConfirmSuggestion}
                            onBookTransfer={handleBookTransfer}
                            onRejectSuggestion={handleRejectSuggestion}
                            onRestoreRejections={handleRestoreRejections}
                            rejectedCount={rejectedCount}
                            onPrefillHistoryBooking={handlePrefillHistoryBooking}
                            onOpenNeighbor={handleOpenNeighbor}
                            prior={prior}
                            llm={llmState}
                            onAskAi={handleAskAi}
                            onUseLlmSuggestion={handleUseLlmSuggestion}
                            onCreateVerification={() => setBooking({ token: Date.now(), manual: true })}
                            booking={booking}
                            onBookingBack={() => setBooking(null)}
                            onBookPayment={handleBookPayment}
                            onBookingSuccess={handleVerificationSuccess}
                            onMarkReconciledExternal={handleMarkReconciledExternal}
                            onConfirmSingle={handleConfirmSingle}
                            linkedVerification={linkedVerification}
                            onReset={handleReset}
                            onShowVerification={handleShowVerification}
                            onAskAssistant={hasAiAssistant ? handleAskAssistant : undefined}
                        />
                        </ReconciliationWorkspace>
                    )}
                    </SideSheet>
                </>
            </div>

            {/* Import modal — evolved preset-driven flow (audit E1/E2/E3). */}
            <BankImportModal
                show={showImportModal}
                onHide={() => setShowImportModal(false)}
                onImportSuccess={handleImportSuccess}
            />

            {/* Shared verification viewer (read-only from this surface). */}
            <VerificationDetailsModal
                showVerificationDetails={showVerificationDetails}
                setShowVerificationDetails={setShowVerificationDetails}
                selectedRow={verificationRow}
                viewOnly
            />

            {/* Avvisa-förslag reason dialog (replaces window.prompt) */}
            <Modal show={!!rejectDialog} onHide={() => !actionBusy && setRejectDialog(null)} centered>
                <Modal.Header closeButton={!actionBusy}>
                    <Modal.Title as="h6">Avvisa förslag</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                        Varför passar förslaget inte? Svaret hjälper systemet att lära sig — men det är valfritt.
                    </p>
                    <textarea
                        className="form-control"
                        rows={3}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="t.ex. fel konto, fel motpart, beloppet stämmer inte …"
                        autoFocus
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setRejectDialog(null)} disabled={actionBusy}>
                        Avbryt
                    </Button>
                    <Button variant="danger" onClick={submitReject} disabled={actionBusy}>
                        {actionBusy ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Avvisar…
                            </>
                        ) : 'Avvisa förslaget'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Card/box/spinner styles come from the promoted design-system
                partial (scss/components/_modern-card.scss, Phase E). */}
        </>
    );
}

// Shared with the Skattekonto page (2026-07-27 rebuild) — same
// visual language, one source of truth.
export { Eyebrow, KebabToggle, KonteringSlip, formatCurrency, formatDate, HistorySuggestionCard, StatementRow, primaryActionFor };
